import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Package, AddOn } from '../../types/Package';
import { loadServicesFromServer, saveServicesToServer } from '../../utils/ServiceAPI';
import { formatCurrency, getCurrencySymbol, convertCurrencyWithMarkup, loadCurrencySettings } from '../../utils/CurrencyUtils';
import { loadAddonsFromServer } from '../../utils/AddonAPI';
import { formatQuoteMoney, priceQuote, QuotePricingSnapshot } from '../../utils/quotePricing';
import { downloadResponseFile } from '../../utils/downloadFile';
import { quoteMailto, renderQuoteHtml } from '../../utils/quoteDocument';
import CountryCodeSelect from './CountryCodeSelect';
import { matchQuoteCategory, packageIsQuotable, packageMatchesShootType, quoteCategoryNames } from '../../utils/quoteCategories';
import { StoredQuote } from '../../utils/quoteRecord';
import {
  ADHOC_PACKAGE_ID,
  AdhocPackage,
  blankSession,
  formatSessionLine,
  isAdhocPackageId,
  preferredFromSessions,
  QuoteSession,
  sessionsFromQuote,
  totalSessionMinutes,
} from '../../utils/quoteSessions';
import { API_BASE_URL } from '../../utils/apiBase';
import ReplyReach, { SentReply } from './ReplyReach';
import PreferredDateField from '../Common/PreferredDateField';
import { formatPreferredDate } from '../../utils/preferredDate';
import ShootLocationField, { PreferredTimeField, EMPTY_SHOOT_LOCATION, hasShootLocation, normalizeShootLocation, ShootLocation } from '../Common/ShootLocationField';
import LetterComposer from './LetterComposer';
import { DURATION_OPTIONS_MINUTES, formatDurationLabel, HALF_HOUR_TIMES } from '../../utils/bookingSchedule';
import './QuoteBuilder.css';

function locationSignature(location?: Partial<ShootLocation> | null): string {
  const place = normalizeShootLocation(location);
  return `${place.name.trim().toLowerCase()}|${place.address.trim().toLowerCase()}|${place.mapsUrl.trim()}`;
}

/** True when the days already name more than one distinct place. */
function sessionsUseSeparatePlaces(rows: QuoteSession[]): boolean {
  const filled = rows
    .map((session) => locationSignature(session.location))
    .filter((key) => key !== '||');
  return new Set(filled).size > 1;
}

function makeAdhocPackageShell(adhoc: AdhocPackage, sessions: QuoteSession[]): Package {
  return {
    id: ADHOC_PACKAGE_ID,
    name: adhoc.name,
    slug: 'adhoc',
    price: sessions.reduce((sum, session) => sum + (Number(session.priceMajor) > 0 ? Number(session.priceMajor) : 0), 0),
    currency: (adhoc.currency || 'R') as Package['currency'],
    durationMinutes: totalSessionMinutes(sessions) || undefined,
    short: adhoc.description || '',
    description: adhoc.description || '',
    features: adhoc.features || [],
    available: false,
    forQuoting: true,
    category: 'Ad hoc',
  };
}

// Module-level flag to prevent debug log spam
let addonsDebugLogged = false;

// Extended interface for selected addons with quantity/hours
interface SelectedAddOn extends AddOn {
  quantity?: number; // For per-each pricing
  hours?: number; // For per-hour pricing
  priceOverridden?: boolean;
  overridePrice?: number;
  overrideCurrency?: string;
}

interface QuoteBuilderProps {
  initialDraft?: StoredQuote | null;
  onDone?: (quote?: StoredQuote) => void;
  returnLabel?: string;
  /** Called with a function that returns false when leaving should be cancelled. */
  onLeaveGuard?: (guard: (() => boolean) | null) => void;
}

export const LEAVE_QUOTATION_MESSAGE = 'Leave this quotation? Anything you have not saved will be lost.';
export const START_OVER_QUOTATION_MESSAGE = 'Start this quotation over? What you have entered will be cleared.';

/** True when leaving the builder would drop a quotation that is not already issued. */
export function quotationWouldBeLost(state: {
  savedForClient: boolean;
  openedExisting: boolean;
  showPreview: boolean;
  hasPackage: boolean;
  step: number;
  hasClientDetails: boolean;
  hasSchedule: boolean;
  hasExtras: boolean;
  hasCategory: boolean;
}): boolean {
  if (state.savedForClient) return false;
  return state.openedExisting
    || state.showPreview
    || state.hasPackage
    || state.step > 0
    || state.hasClientDetails
    || state.hasSchedule
    || state.hasExtras
    || state.hasCategory;
}

function addonEditKey(rows?: unknown[]) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const item = row as Record<string, unknown>;
    return {
      id: String(item.id || item.name || ''),
      quantity: Number(item.quantity || 0),
      hours: Number(item.hours || 0),
      priceOverridden: Boolean(item.priceOverridden),
      overridePrice: item.overridePrice ?? null,
      overrideCurrency: String(item.overrideCurrency || ''),
    };
  });
}

function sessionEditKey(rows?: QuoteSession[] | null) {
  return (Array.isArray(rows) ? rows : []).map((session) => ({
    id: String(session.id || ''),
    label: String(session.label || ''),
    date: String(session.date || ''),
    startTime: String(session.startTime || ''),
    durationMinutes: Number(session.durationMinutes || 0),
    priceMajor: session.priceMajor == null ? null : Number(session.priceMajor),
    location: normalizeShootLocation(session.location),
    notes: String(session.notes || ''),
  }));
}

/** What the editor can change. Used to skip a new version when Save is pressed with no edits. */
function editSignature(payload: {
  client: { name?: string; email?: string; company?: string; billingAddress?: string; countryCode?: string; phone?: string; preference?: string; message?: string };
  shootType?: string;
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: { name?: string; address?: string; mapsUrl?: string };
  sessions?: QuoteSession[];
  adhocPackage?: AdhocPackage | null;
  guideIds?: string[];
  draft: {
    packageId?: string;
    currency?: string;
    discountPercent?: number;
    depositPercent?: number;
    overridePackagePrice?: number | null;
    overridePackageCurrency?: string | null;
    selectedAddOns?: unknown[];
    customAddons?: unknown[];
  };
}) {
  const place = normalizeShootLocation(payload.preferredLocation);
  return JSON.stringify({
    name: String(payload.client.name || '').trim(),
    email: String(payload.client.email || '').trim(),
    company: String(payload.client.company || '').trim(),
    billingAddress: String(payload.client.billingAddress || '').trim(),
    countryCode: payload.client.countryCode || '',
    phone: String(payload.client.phone || '').trim(),
    preference: payload.client.preference || '',
    message: String(payload.client.message || '').trim(),
    shootType: payload.shootType || '',
    preferredDate: payload.preferredDate || '',
    preferredTime: payload.preferredTime || '',
    location: { name: place.name, address: place.address, mapsUrl: place.mapsUrl },
    sessions: sessionEditKey(payload.sessions),
    adhocPackage: payload.adhocPackage
      ? {
          name: payload.adhocPackage.name || '',
          description: payload.adhocPackage.description || '',
          features: payload.adhocPackage.features || [],
          currency: payload.adhocPackage.currency || '',
        }
      : null,
    guideIds: Array.isArray(payload.guideIds) ? [...payload.guideIds].sort() : [],
    packageId: payload.draft.packageId || '',
    currency: payload.draft.currency || '',
    discountPercent: Number(payload.draft.discountPercent || 0),
    depositPercent: Number(payload.draft.depositPercent ?? 0),
    overridePackagePrice: payload.draft.overridePackagePrice ?? null,
    overridePackageCurrency: payload.draft.overridePackageCurrency || '',
    addons: addonEditKey(payload.draft.selectedAddOns),
    custom: addonEditKey(payload.draft.customAddons),
  });
}

interface FilterAnswers {
  category?: string;
}

/**
 * A short, client-facing description of the package for the quotation:
 * one line of text and at most a handful of "what's included" bullets.
 */
function packageSummary(pkg: Package): { description?: string; features?: string[]; durationMinutes?: number } {
  const short = (pkg.short || '').trim();
  const long = (pkg.description || '').trim();
  let description = short || long;
  if (description.length > 200) description = `${description.slice(0, 197).replace(/\s+\S*$/, '')}…`;
  const features = (pkg.features || []).map((item) => String(item).trim()).filter(Boolean).slice(0, 5);
  const durationMinutes = Math.round(Number(pkg.durationMinutes));
  return {
    ...(description ? { description } : {}),
    ...(features.length > 0 ? { features } : {}),
    ...(Number.isFinite(durationMinutes) && durationMinutes > 0 ? { durationMinutes } : {}),
  };
}


const QuoteBuilder: React.FC<QuoteBuilderProps> = ({ initialDraft, onDone, returnLabel = 'Back', onLeaveGuard }) => {
  const fromEnquiry = Boolean(initialDraft && !initialDraft.id);
  const [packages, setPackages] = useState<Package[]>([]);
  const [allPackages, setAllPackages] = useState<Package[]>([]);
  const [filterAnswers, setFilterAnswers] = useState<FilterAnswers>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);
  const [globalAddons, setGlobalAddons] = useState<AddOn[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD'>('R');
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [quoteHTML, setQuoteHTML] = useState<string>('');
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [personalMessage, setPersonalMessage] = useState<string>('');
  const [guideLibrary, setGuideLibrary] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedGuideIds, setSelectedGuideIds] = useState<string[]>([]);
  const [clientPhone, setClientPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+27');
  const [contactPreference, setContactPreference] = useState<'email' | 'whatsapp' | 'either'>('either');
  const [companyName, setCompanyName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [preferredLocation, setPreferredLocation] = useState<ShootLocation>({ ...EMPTY_SHOOT_LOCATION });
  const [sessions, setSessions] = useState<QuoteSession[]>([]);
  const [placesArePerDay, setPlacesArePerDay] = useState(false);
  const sessionsRootRef = useRef<HTMLDivElement>(null);
  const scrollToNewDay = useRef(false);
  const [adhocPackage, setAdhocPackage] = useState<AdhocPackage | null>(null);
  const [buildingAdhoc, setBuildingAdhoc] = useState(false);
  const [adhocDraft, setAdhocDraft] = useState({ name: '', description: '', features: '' });
  const [contactError, setContactError] = useState('');
  const [discount, setDiscount] = useState<number>(0); // Discount percentage (0-100)
  const [depositPercent, setDepositPercent] = useState<number>(50);
  const [subtotal, setSubtotal] = useState<number>(0); // Subtotal before discount
  const [pricingSnapshot, setPricingSnapshot] = useState<QuotePricingSnapshot | null>(null);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [pdfNote, setPdfNote] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [catalogueWarnings, setCatalogueWarnings] = useState<string[]>([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [settingUpPackage, setSettingUpPackage] = useState(false);
  const [savingPackage, setSavingPackage] = useState(false);
  const [packageSetupError, setPackageSetupError] = useState('');
  const [packageDraft, setPackageDraft] = useState({
    name: '',
    category: '',
    price: '',
    durationMinutes: '',
    short: '',
    features: '',
    showOnWebsite: false,
  });
  const [issuedReach, setIssuedReach] = useState<{ quoteId: string; sent: SentReply } | null>(null);
  const issuedReachRef = useRef<HTMLDivElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [clientLink, setClientLink] = useState<string>('');
  const [overridePackagePrice, setOverridePackagePrice] = useState<number | null>(null); // Override package price
  const [overridePackageCurrency, setOverridePackageCurrency] = useState<string | null>(null);
  const [overriddenPackageAddons, setOverriddenPackageAddons] = useState<{
    removed: string[]; // IDs/names of addons to remove from package
    added: SelectedAddOn[]; // Custom addons to add to package
    priceOverrides: Map<string, number>; // Overridden prices for addons
  }>({ removed: [], added: [], priceOverrides: new Map() });
  const [displayBasePrice, setDisplayBasePrice] = useState<number>(0); // Base price shown in review (converted)
  // Track in-progress addon price inputs so typing isn't overridden by conversions
  const [addonPriceInputs, setAddonPriceInputs] = useState<Map<string, string>>(new Map());
  const [contactInfo, setContactInfo] = useState<{
    email?: string;
    phone?: string;
    location?: string;
  }>({});
  const [galleryCategories, setGalleryCategories] = useState<any[]>([]);

  const currencies: Array<{ value: 'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD'; label: string; icon: string }> = [
    { value: 'R', label: 'South African Rand', icon: 'fas fa-rand-sign' },
    { value: 'USD', label: 'US Dollar', icon: 'fas fa-dollar-sign' },
    { value: 'EUR', label: 'Euro', icon: 'fas fa-euro-sign' },
    { value: 'GBP', label: 'British Pound', icon: 'fas fa-pound-sign' },
    { value: 'CAD', label: 'Canadian Dollar', icon: 'fas fa-dollar-sign' },
    { value: 'AUD', label: 'Australian Dollar', icon: 'fas fa-dollar-sign' }
  ];

  const catalogueCategories = useMemo(() => quoteCategoryNames(allPackages), [allPackages]);

  // Quotation choices come from the package catalogue. Gallery categories are only a fallback before that list loads.
  const categoryOptions = useMemo(() => {
    if (catalogueCategories.length > 0) {
      const fromCatalogue = catalogueCategories.map((name) => ({
        id: name,
        label: name,
        icon: 'fas fa-camera',
        color: '#242424',
      }));
      fromCatalogue.push({ id: 'any', label: 'No preference', icon: 'fas fa-question-circle', color: '#6C757D' });
      return fromCatalogue;
    }
    const dynamicOptions = galleryCategories.map(cat => {
      // Map category names to icons
      const iconMap: { [key: string]: string } = {
        'wedding': 'fas fa-heart',
        'portrait': 'fas fa-user-circle',
        'family': 'fas fa-users',
        'business': 'fas fa-briefcase',
        'event': 'fas fa-calendar-star',
        'newborn': 'fas fa-baby',
        'engagement': 'fas fa-ring',
        'maternity': 'fas fa-baby-carriage',
        'corporate': 'fas fa-building',
      };
      
      const defaultIcon = 'fas fa-camera';
      const categoryKey = cat.slug.toLowerCase();
      const icon = iconMap[categoryKey] || defaultIcon;
      
      return {
        id: cat.name, // Use category name as ID for matching
        label: cat.name,
        icon: icon,
        color: '#242424'
      };
    });
    
    // Add "No Preference" option
    dynamicOptions.push({ id: 'any', label: 'No Preference', icon: 'fas fa-question-circle', color: '#595959' });
    
    return dynamicOptions;
  }, [catalogueCategories, galleryCategories]);

  // Filter questions - simplified to just category
  const filterQuestions = useMemo(() => [
    {
      id: 'category',
      title: 'Quotation',
      options: categoryOptions
    }
  ], [categoryOptions]);

  const filterStepCount = filterQuestions.length;

  // Get package addons (included in the package) - defined early so it can be used in getTotalSteps
  const getPackageAddons = (pkg: Package): AddOn[] => {
    const baseAddons = pkg.addons || [];
    // Filter out removed addons and add custom addons
    const filtered = baseAddons.filter(addon => {
      const key = addon.id || addon.name;
      return !overriddenPackageAddons.removed.includes(key);
    });
    // Add custom addons
    const customAddons = overriddenPackageAddons.added.map(addon => ({
      ...addon,
      id: addon.id || `custom-${addon.name}`,
    })) as AddOn[];
    return [...filtered, ...customAddons];
  };

  // Get additional addons (global addons that aren't already in the package) - defined early so it can be used in getTotalSteps
  const getAdditionalAddons = (pkg: Package): AddOn[] => {
    const packageAddonIds = new Set<string>();
    const packageAddonNames = new Set<string>();
    
    // Track package addon IDs and names
    if (pkg.addons) {
      pkg.addons.forEach(pkgAddon => {
        if (pkgAddon.id) {
          packageAddonIds.add(pkgAddon.id);
        }
        if (pkgAddon.name) {
          packageAddonNames.add(pkgAddon.name.toLowerCase());
        }
      });
    }
    
    // Return global addons that aren't in the package
    return globalAddons.filter(globalAddon => {
      // Check by ID first
      if (globalAddon.id && packageAddonIds.has(globalAddon.id)) {
        return false;
      }
      // Check by name
      if (globalAddon.name && packageAddonNames.has(globalAddon.name.toLowerCase())) {
        return false;
      }
      return true;
    });
  };

  // Calculate total steps based on package features
  const getTotalSteps = () => {
    let steps = filterStepCount; // Filter steps
    steps++; // Package selection
    steps++; // Currency
    if (selectedPackage) {
      const packageAddons = getPackageAddons(selectedPackage);
      const additionalAddons = getAdditionalAddons(selectedPackage);
      if (packageAddons.length > 0 || additionalAddons.length > 0) {
        steps++; // Addons step
      }
    }
    steps++; // Client details step
    steps++; // Review step
    return steps;
  };

  const totalSteps = getTotalSteps();

  useEffect(() => {
    loadPackages();
    loadContactInfo();
    loadCategories();
    loadGlobalAddons();
  }, []);

  // Reset in-progress price inputs when package or currency changes
  useEffect(() => {
    setAddonPriceInputs(new Map());
  }, [selectedPackage, selectedCurrency]);

  // Keep display base price converted to selected currency (honors overrides)
  useEffect(() => {
    const updateDisplayBase = async () => {
      if (!selectedPackage) {
        setDisplayBasePrice(0);
        return;
      }
      // The catalogue price, in the selected currency. A typed override is shown separately.
      const baseRaw = selectedPackage.specialPrice || selectedPackage.price || 0;
      const fromCurrency = selectedPackage.currency || 'R';
      if (fromCurrency === selectedCurrency) {
        setDisplayBasePrice(baseRaw);
        return;
      }
      try {
        const currencySettings = loadCurrencySettings();
        const result = await convertCurrencyWithMarkup(
          baseRaw,
          fromCurrency,
          selectedCurrency,
          currencySettings
        );
        setDisplayBasePrice(result.markupAmount);
      } catch (err) {
        console.error('Error converting base price for display:', err);
        setDisplayBasePrice(baseRaw);
      }
    };
    updateDisplayBase();
  }, [selectedPackage, selectedCurrency]);

  // Load global addons
  const loadGlobalAddons = async () => {
    try {
      const addons = await loadAddonsFromServer();
      console.log('📦 QuoteBuilder: Loaded addons from server:', addons.length);
      console.log('📦 Addon details:', JSON.stringify(addons, null, 2));
      console.log('📦 Addon names:', addons.map(a => a.name));
      setGlobalAddons(addons);
    } catch (error) {
      console.error('Error loading global addons:', error);
      setGlobalAddons([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/studio/guides`, { credentials: 'include' });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) return;
        const rows = Array.isArray(body.guides) ? body.guides : [];
        setGuideLibrary(rows.map((row: { id?: string; title?: string }) => ({
          id: String(row.id || ''),
          title: String(row.title || 'Guide'),
        })).filter((row: { id: string }) => row.id));
      } catch (_) {
        if (!cancelled) setGuideLibrary([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Helper function to get CSS variable value
  const getCSSVariable = (variableName: string, fallback: string = '#000000'): string => {
    if (typeof window === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return value || fallback;
  };

  // Load theme colors and apply to CSS variables
  useEffect(() => {
    const loadThemeColors = () => {
      try {
        const themeData = localStorage.getItem('admin-theme');
        const root = document.documentElement;
        
        if (themeData) {
          const theme = JSON.parse(themeData);
          if (theme.palette) {
            const primaryColor = theme.palette.primary || theme.palette.text || '#000000';
            const textColor = theme.palette.text || '#000000';
            
            root.style.setProperty('--color-primary', primaryColor);
            root.style.setProperty('--color-secondary', theme.palette.secondary || '#1A1A1A');
            root.style.setProperty('--color-accent', theme.palette.accent || '#333333');
            root.style.setProperty('--color-text', textColor);
            root.style.setProperty('--color-muted', theme.palette.muted || '#6C757D');
            root.style.setProperty('--color-border', theme.palette.border || '#E1E5E9');
            root.style.setProperty('--color-surface', theme.palette.surface || '#F8F9FA');
            
            // Update RGB values for rgba() usage - handle both hex and rgb formats
            let r = 0, g = 0, b = 0;
            const hexMatch = primaryColor.match(/#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
            if (hexMatch) {
              r = parseInt(hexMatch[1], 16);
              g = parseInt(hexMatch[2], 16);
              b = parseInt(hexMatch[3], 16);
            } else {
              const rgbMatch = primaryColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
              if (rgbMatch) {
                r = parseInt(rgbMatch[1], 10);
                g = parseInt(rgbMatch[2], 10);
                b = parseInt(rgbMatch[3], 10);
              }
            }
            root.style.setProperty('--qb-primary-rgb', `${r}, ${g}, ${b}`);
          }
        } else {
          // Default to minimal black theme
          root.style.setProperty('--color-primary', '#000000');
          root.style.setProperty('--color-text', '#000000');
          root.style.setProperty('--qb-primary-rgb', '0, 0, 0');
        }
      } catch (error) {
        console.error('Error loading theme colors:', error);
      }
    };
    
    loadThemeColors();
    
    // Listen for theme updates
    const handleThemeUpdate = () => {
      loadThemeColors();
    };
    
    window.addEventListener('theme_updated', handleThemeUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'admin-theme' || e.key === null) {
        loadThemeColors();
      }
    });
    
    return () => {
      window.removeEventListener('theme_updated', handleThemeUpdate);
    };
  }, []);

  // Listen for category updates
  useEffect(() => {
    const handleCategoryUpdate = () => {
      loadCategories();
    };
    
    window.addEventListener('categories_updated', handleCategoryUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'admin_categories' || e.key === null) {
        loadCategories();
      }
    });
    
    return () => {
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Listen for addon updates
  useEffect(() => {
    const handleAddonUpdate = () => {
      loadGlobalAddons();
    };
    
    window.addEventListener('addons_updated', handleAddonUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'admin_addons' || e.key === null) {
        loadGlobalAddons();
      }
    });
    
    return () => {
      window.removeEventListener('addons_updated', handleAddonUpdate);
    };
  }, []);

  useEffect(() => {
    filterPackages();
  }, [filterAnswers, allPackages]);

  const pendingReviewId = useRef<string | null>(null);
  const draftApplied = useRef('');
  const issuedSourceId = useRef('');
  const issuedBaseline = useRef('');
  const skipAddonSync = useRef(false);

  useEffect(() => {
    if (!initialDraft || !packagesLoaded) return;
    const draftKey = initialDraft.id || 'enquiry';
    if (draftApplied.current === draftKey) return;
    draftApplied.current = draftKey;
    const editingLive = initialDraft.status === 'issued' || initialDraft.status === 'accepted';
    if (initialDraft.status && initialDraft.status !== 'draft' && !editingLive) {
      setSaveMessage(`${initialDraft.quoteNumber} is ${initialDraft.status} and cannot be edited as a draft.`);
      setCatalogueWarnings([]);
      return;
    }
    const draft = (initialDraft.draft || {}) as Record<string, any>;
    // Enquiry prefill (no persisted quotation id yet)
    if (!initialDraft.id) {
      setClientName(draft.clientName || initialDraft.client?.name || '');
      setPersonalMessage(draft.personalMessage || initialDraft.client?.message || '');
      setRecipientEmail(draft.recipientEmail || initialDraft.client?.email || '');
      setClientPhone(draft.clientPhone || initialDraft.client?.phoneE164 || '');
      setCountryCode(draft.countryCode || initialDraft.client?.countryCode || '+27');
      setContactPreference(draft.contactPreference || initialDraft.client?.preference || 'either');
      setCompanyName(draft.companyName || initialDraft.client?.company || '');
      setBillingAddress(draft.billingAddress || initialDraft.client?.billingAddress || '');
      setPreferredDate(draft.preferredDate || initialDraft.preferredDate || '');
      setPreferredTime(draft.preferredTime || initialDraft.preferredTime || '');
      setPreferredLocation(normalizeShootLocation(draft.preferredLocation || initialDraft.preferredLocation));
      const restoredEnquirySessions = sessionsFromQuote({
        sessions: draft.sessions || initialDraft.sessions,
        preferredDate: draft.preferredDate || initialDraft.preferredDate,
        preferredTime: draft.preferredTime || initialDraft.preferredTime,
        preferredLocation: draft.preferredLocation || initialDraft.preferredLocation,
        snapshot: initialDraft.snapshot,
        adhocPackage: draft.adhocPackage || initialDraft.adhocPackage,
      });
      setSessions(restoredEnquirySessions);
      setPlacesArePerDay(sessionsUseSeparatePlaces(restoredEnquirySessions));
      setAdhocPackage(draft.adhocPackage || initialDraft.adhocPackage || null);
      setSelectedGuideIds(Array.isArray(initialDraft.guideIds) ? initialDraft.guideIds.map(String) : []);
      const matched = matchQuoteCategory(
        String(draft.shootType || initialDraft.shootType || ''),
        quoteCategoryNames(allPackages),
      );
      setFilterAnswers(matched ? { category: matched } : {});
      setCatalogueWarnings([]);
      setSaveMessage('Choose the quotation and a package, then fill in the details.');
      return;
    }
    const warnings: string[] = [];
    const savedPackageId = String(draft.packageId || '').trim();
    const restoredAdhoc = (draft.adhocPackage || initialDraft.adhocPackage || null) as AdhocPackage | null;
    const restoredSessions = sessionsFromQuote({
      sessions: draft.sessions || initialDraft.sessions,
      preferredDate: draft.preferredDate || initialDraft.preferredDate,
      preferredTime: draft.preferredTime || initialDraft.preferredTime,
      preferredLocation: draft.preferredLocation || initialDraft.preferredLocation,
      snapshot: initialDraft.snapshot,
      adhocPackage: restoredAdhoc,
    });
    const pkg = isAdhocPackageId(savedPackageId) && restoredAdhoc
      ? makeAdhocPackageShell(restoredAdhoc, restoredSessions)
      : savedPackageId
        ? (allPackages.find((item) => item.id === savedPackageId) || null)
        : null;
    if (savedPackageId && !pkg && !isAdhocPackageId(savedPackageId)) {
      warnings.push(
        `Saved package is no longer in the catalogue (id: ${savedPackageId}${draft.shootType ? `, shoot: ${draft.shootType}` : ''}). Choose a package — nothing was substituted.`,
      );
    } else if (!savedPackageId && (draft.shootType || draft.overridePackagePrice != null)) {
      warnings.push('This draft has no saved package id. Choose a package before issuing — nothing was auto-selected.');
    }
    const restoredAddOns = (draft.selectedAddOns || []) as unknown as SelectedAddOn[];
    const restoredCustom = (draft.customAddons || []) as unknown as SelectedAddOn[];
    const knownAddonIds = new Set(
      allPackages.flatMap((item) => [
        ...((item as any).addons || []).map((addon: { id?: string }) => addon.id),
        ...((item as any).includedAddons || []).map((addon: { id?: string }) => addon.id),
      ].filter(Boolean)).concat(
        // global addons arrive later; warn only when id is clearly absent from package-scoped lists
      ),
    );
    [...restoredAddOns, ...restoredCustom].forEach((addon) => {
      const addonId = String((addon as any)?.id || '').trim();
      if (!addonId) return;
      if (pkg && knownAddonIds.has(addonId)) return;
      const label = String((addon as any)?.name || addonId);
      if (!warnings.some((line) => line.includes(label))) {
        warnings.push(`Saved add-on “${label}” is shown from the draft. Confirm it still belongs on the package.`);
      }
    });
    pendingReviewId.current = initialDraft.id;
    skipAddonSync.current = true;
    if (editingLive) {
      issuedSourceId.current = initialDraft.id;
      const shootType = String(draft.shootType || initialDraft.shootType || '');
      const place = normalizeShootLocation(draft.preferredLocation || initialDraft.preferredLocation);
      issuedBaseline.current = editSignature({
        client: {
          name: String(draft.clientName || initialDraft.client?.name || ''),
          email: String(draft.recipientEmail || initialDraft.client?.email || ''),
          company: String(draft.companyName || initialDraft.client?.company || ''),
          billingAddress: String(draft.billingAddress || initialDraft.client?.billingAddress || ''),
          countryCode: String(draft.countryCode || initialDraft.client?.countryCode || '+27'),
          phone: String(draft.clientPhone || initialDraft.client?.phoneE164 || ''),
          preference: String(draft.contactPreference || initialDraft.client?.preference || 'either'),
          message: String(draft.personalMessage || initialDraft.client?.message || ''),
        },
        shootType,
        preferredDate: String(draft.preferredDate || initialDraft.preferredDate || ''),
        preferredTime: String(draft.preferredTime || initialDraft.preferredTime || ''),
        preferredLocation: place,
        sessions: restoredSessions,
        adhocPackage: restoredAdhoc,
        guideIds: Array.isArray(initialDraft.guideIds) ? initialDraft.guideIds.map(String) : [],
        draft: {
          packageId: savedPackageId,
          currency: String(draft.currency || initialDraft.snapshot?.currency || 'R'),
          discountPercent: Number(draft.discountPercent || 0),
          depositPercent: Number(draft.depositPercent ?? initialDraft.snapshot?.depositPercent ?? 50),
          overridePackagePrice: draft.overridePackagePrice != null ? draft.overridePackagePrice : null,
          overridePackageCurrency: String(draft.overridePackageCurrency ?? initialDraft.snapshot?.currency ?? 'R'),
          selectedAddOns: restoredAddOns,
          customAddons: restoredCustom,
        },
      });
      setSavedQuoteId('');
    } else {
      issuedSourceId.current = '';
      issuedBaseline.current = '';
      setSavedQuoteId(initialDraft.id);
    }
    setAdhocPackage(restoredAdhoc);
    setSessions(restoredSessions);
    setPlacesArePerDay(sessionsUseSeparatePlaces(restoredSessions));
    setSelectedPackage(pkg);
    setSelectedCurrency(draft.currency || initialDraft.snapshot?.currency || 'R');
    setDiscount(draft.discountPercent || 0);
    setDepositPercent(draft.depositPercent ?? initialDraft.snapshot?.depositPercent ?? 50);
    setOverridePackagePrice(
      draft.overridePackagePrice != null
        ? draft.overridePackagePrice
        : null,
    );
    setOverridePackageCurrency(draft.overridePackageCurrency ?? initialDraft.snapshot?.currency ?? 'R');
    setSelectedAddOns(restoredAddOns);
    setOverriddenPackageAddons({
      removed: [],
      added: restoredCustom,
      priceOverrides: new Map(),
    });
    setClientName(draft.clientName || initialDraft.client?.name || '');
    setPersonalMessage(draft.personalMessage || initialDraft.client?.message || '');
    setRecipientEmail(draft.recipientEmail || initialDraft.client?.email || '');
    setClientPhone(draft.clientPhone || initialDraft.client?.phoneE164 || '');
    setCountryCode(draft.countryCode || initialDraft.client?.countryCode || '+27');
    setContactPreference(draft.contactPreference || initialDraft.client?.preference || 'either');
    setCompanyName(draft.companyName || initialDraft.client?.company || '');
    setBillingAddress(draft.billingAddress || initialDraft.client?.billingAddress || '');
    setPreferredDate(draft.preferredDate || initialDraft.preferredDate || '');
    setPreferredTime(draft.preferredTime || initialDraft.preferredTime || '');
    setPreferredLocation(normalizeShootLocation(draft.preferredLocation || initialDraft.preferredLocation));
    setSelectedGuideIds(Array.isArray(initialDraft.guideIds) ? initialDraft.guideIds.map(String) : []);
    setFilterAnswers(draft.shootType || initialDraft.shootType ? { category: draft.shootType || initialDraft.shootType } : {});
    setCatalogueWarnings(warnings);
    setSaveMessage(editingLive
      ? `Editing ${initialDraft.quoteNumber}. Save to apply changes on this job. It stays ${initialDraft.status}.`
      : `Editing ${initialDraft.quoteNumber} (draft v${initialDraft.version})`);
  }, [initialDraft, allPackages, packagesLoaded]);

  useEffect(() => {
    if (selectedPackage) {
      calculateTotal();
    }
  }, [selectedPackage, selectedAddOns, selectedCurrency, discount, depositPercent, overridePackagePrice, overridePackageCurrency, sessions, adhocPackage]);

  useEffect(() => {
    if (!scrollToNewDay.current) return;
    scrollToNewDay.current = false;
    const rows = sessionsRootRef.current?.querySelectorAll('.quote-session-row');
    const last = rows && rows.length ? rows[rows.length - 1] : null;
    if (last instanceof HTMLElement) last.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [sessions.length]);

  useEffect(() => {
    if (!pendingReviewId.current || !selectedPackage) return;
    setCurrentStep(getTotalSteps() - 1);
    pendingReviewId.current = null;
  }, [selectedPackage, globalAddons]);

  // State to store converted addon prices
  const [convertedAddonPrices, setConvertedAddonPrices] = useState<Map<string, number>>(new Map());

  // Convert addon price to selected currency
  const getConvertedAddonPrice = useCallback(async (addon: AddOn): Promise<number> => {
    if (!selectedPackage || !addon.price || addon.price === 0) {
      return addon.price || 0;
    }

    const addonCurrency = addon.currency || selectedPackage.currency || 'R';
    
    // If currencies match, no conversion needed
    if (addonCurrency === selectedCurrency) {
      return addon.price;
    }

    try {
      const currencySettings = loadCurrencySettings();
      const result = await convertCurrencyWithMarkup(
        addon.price,
        addonCurrency,
        selectedCurrency,
        currencySettings
      );
      return result.markupAmount;
    } catch (error) {
      console.error('Error converting addon price:', error);
      return addon.price; // Fallback to original price
    }
  }, [selectedPackage, selectedCurrency]);

  // Update converted addon prices when currency or addons change (respects overrides)
  useEffect(() => {
    if (!selectedPackage) {
      setConvertedAddonPrices(new Map());
      return;
    }

    const convertAmount = async (amount: number, fromCurrency: string): Promise<number> => {
      if (fromCurrency === selectedCurrency) return amount;
      try {
        const currencySettings = loadCurrencySettings();
        const result = await convertCurrencyWithMarkup(
          amount,
          fromCurrency,
          selectedCurrency,
          currencySettings
        );
        return result.markupAmount;
      } catch (error) {
        console.error('Error converting addon price:', error);
        return amount;
      }
    };

    const updateConvertedPrices = async () => {
      const priceMap = new Map<string, number>();
      
      // Convert package addons
      const packageAddons = getPackageAddons(selectedPackage);
      for (const addon of packageAddons) {
        const key = addon.id || addon.name;
        const override = selectedAddOns.find(a => (a.id || a.name) === key);
        const basePrice = override?.priceOverridden ? (override.overridePrice ?? 0) : (addon.price ?? 0);
        const fromCurrency = override?.priceOverridden
          ? (override.overrideCurrency || selectedCurrency)
          : (addon.currency || selectedPackage.currency || 'R');
        const convertedPrice = await convertAmount(basePrice, fromCurrency);
        priceMap.set(key, convertedPrice);
      }

      // Convert additional addons
      const additionalAddons = getAdditionalAddons(selectedPackage);
      for (const addon of additionalAddons) {
        const key = addon.id || addon.name;
        const override = selectedAddOns.find(a => (a.id || a.name) === key);
        const basePrice = override?.priceOverridden ? (override.overridePrice ?? 0) : (addon.price ?? 0);
        const fromCurrency = override?.priceOverridden
          ? (override.overrideCurrency || selectedCurrency)
          : (addon.currency || selectedPackage.currency || 'R');
        const convertedPrice = await convertAmount(basePrice, fromCurrency);
        priceMap.set(key, convertedPrice);
      }

      setConvertedAddonPrices(priceMap);
    };

    updateConvertedPrices();
  }, [selectedPackage, selectedCurrency, globalAddons, selectedAddOns]);

  const loadPackages = async () => {
    try {
      const loadedPackages = await loadServicesFromServer();
      console.log('📦 QuoteBuilder: Loaded packages from server:', loadedPackages.length);
      const availablePackages = loadedPackages.filter(packageIsQuotable);
      console.log('📦 Available packages:', availablePackages.length);
      availablePackages.forEach(pkg => {
        console.log(`📦 Package "${pkg.name}":`, {
          id: pkg.id,
          addons: pkg.addons?.length || 0,
          addonDetails: pkg.addons?.map(a => ({ id: a.id, name: a.name, price: a.price }))
        });
      });
      setAllPackages(availablePackages);
      setPackages(availablePackages);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setPackagesLoaded(true);
    }
  };

  const loadCategories = async () => {
    try {
      const { loadCategoriesFromServer } = await import('../../utils/categories');
      const categories = await loadCategoriesFromServer();
      setGalleryCategories(categories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setGalleryCategories([]);
    }
  };

  const filterPackages = () => {
    let filtered = [...allPackages];

    // Category filter
    if (filterAnswers.category && filterAnswers.category !== 'any') {
      const selectedCategoryName = filterAnswers.category;
      filtered = filtered.filter(pkg => packageMatchesShootType(pkg, selectedCategoryName));
    }


    setPackages(filtered);
  };

  const loadContactInfo = () => {
    try {
      const websiteContent = localStorage.getItem('website_content');
      if (websiteContent) {
        const parsed = JSON.parse(websiteContent);
        setContactInfo({
          email: parsed.contactEmail || '',
          phone: parsed.contactPhone || '',
          location: parsed.contactLocation || ''
        });
      }
    } catch (error) {
      console.error('Error loading contact info:', error);
      setContactInfo({});
    }
  };

  const buildSnapshot = async (): Promise<QuotePricingSnapshot | null> => {
    if (!selectedPackage) return null;
    const usingAdhoc = Boolean(adhocPackage && isAdhocPackageId(selectedPackage.id));
    const schedule = sessions.length > 0
      ? sessions
      : sessionsFromQuote({
          preferredDate,
          preferredTime,
          preferredLocation,
          snapshot: null,
          adhocPackage,
        });
    const packageAmount = selectedPackage.specialPrice || selectedPackage.price || 0;
    const addonLines = selectedAddOns.map((addon) => ({
      id: addon.id || addon.name,
      kind: (addon.id || '').startsWith('custom-') ? 'custom' as const : 'extra' as const,
      name: addon.name,
      description: addon.description,
      pricingType: (addon.pricingType || 'fixed') as 'fixed' | 'per-hour' | 'per-each',
      units: addon.pricingType === 'per-hour'
        ? (addon.hours || 1)
        : addon.pricingType === 'per-each'
          ? (addon.quantity || 1)
          : 1,
      unitAmount: addon.price || 0,
      sourceCurrency: addon.currency || selectedPackage.currency || 'R',
      overrideUnitAmount: addon.priceOverridden ? addon.overridePrice : null,
      overrideCurrency: addon.priceOverridden ? addon.overrideCurrency : null,
    }));
    const packageOverride = overridePackagePrice != null && Number.isFinite(overridePackagePrice) && overridePackagePrice >= 0
      ? overridePackagePrice
      : null;
    const lines = usingAdhoc
      ? [
          {
            id: 'package',
            kind: 'package' as const,
            name: adhocPackage!.name,
            description: adhocPackage!.description,
            features: adhocPackage!.features,
            durationMinutes: totalSessionMinutes(schedule) || undefined,
            pricingType: 'fixed' as const,
            units: 1,
            unitAmount: packageOverride != null ? packageOverride : 0,
            sourceCurrency: packageOverride != null ? (overridePackageCurrency || selectedCurrency) : selectedCurrency,
            complimentary: packageOverride == null,
            ...(packageOverride != null ? {
              overrideUnitAmount: packageOverride,
              overrideCurrency: overridePackageCurrency || selectedCurrency,
            } : {}),
          },
          ...schedule.map((session, index) => ({
            id: session.id || `session-${index + 1}`,
            kind: 'session' as const,
            name: String(session.label || '').trim() || `Day ${index + 1}`,
            sessionId: session.id,
            date: session.date || '',
            startTime: session.startTime || '',
            durationMinutes: Number(session.durationMinutes) > 0 ? Math.round(Number(session.durationMinutes)) : undefined,
            ...(String(session.notes || '').trim() ? { description: String(session.notes).trim() } : {}),
            pricingType: 'fixed' as const,
            units: 1,
            unitAmount: Number(session.priceMajor) > 0 ? Number(session.priceMajor) : 0,
            sourceCurrency: selectedCurrency,
          })),
          ...addonLines,
        ]
      : [
          {
            id: 'package',
            kind: 'package' as const,
            name: selectedPackage.name,
            ...packageSummary(selectedPackage),
            pricingType: 'fixed' as const,
            units: 1,
            unitAmount: packageAmount,
            sourceCurrency: selectedPackage.currency || 'R',
            overrideUnitAmount: packageOverride,
            overrideCurrency: packageOverride != null ? (overridePackageCurrency || selectedCurrency) : null,
          },
          ...addonLines,
        ];
    return priceQuote({
      currency: selectedCurrency,
      discountPercent: discount,
      depositPercent,
      lines,
      convert: async (amount, fromCurrency, toCurrency) => {
        const result = await convertCurrencyWithMarkup(
          amount,
          fromCurrency as 'R',
          toCurrency as 'R',
          loadCurrencySettings()
        );
        return result;
      },
    });
  };

  const calculateTotal = async () => {
    if (!selectedPackage) {
      setTotalPrice(0);
      setSubtotal(0);
      setPricingSnapshot(null);
      setDisplayBasePrice(0);
      return;
    }

    setIsCalculating(true);
    try {
      const snapshot = await buildSnapshot();
      if (!snapshot) return;
      setPricingSnapshot(snapshot);
      setSubtotal(snapshot.quoteSubtotalMinor / 100);
      setTotalPrice(snapshot.quoteTotalMinor / 100);
    } catch (error) {
      console.error('Error calculating total:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleFilterAnswer = (questionId: string, optionId: string) => {
    setFilterAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));

    // Auto-advance to next step after a short delay
    if (currentStep < filterStepCount - 1) {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 300);
    } else if (currentStep === filterStepCount - 1) {
      // Last filter question - move to package selection after delay
      setTimeout(() => {
        setCurrentStep(filterStepCount);
      }, 300);
    }
  };

  const handlePackageSelect = (pkg: Package) => {
    // Coming back to change something else and choosing the same package must not wipe the extras already picked.
    if (selectedPackage && selectedPackage.id === pkg.id) {
      setSelectedPackage(pkg);
      if (!fromEnquiry) setCurrentStep(filterStepCount + 1);
      return;
    }
    setAdhocPackage(null);
    setBuildingAdhoc(false);
    setSelectedPackage(pkg);
    // Calculate package duration in hours (exact decimal hours)
    const packageDurationHours = pkg.durationMinutes ? pkg.durationMinutes / 60 : undefined;
    
    // Auto-select package addons when package is selected (with overrides applied)
    const basePackageAddons = pkg.addons || [];
    const filteredAddons = basePackageAddons.filter(addon => {
      const key = addon.id || addon.name;
      return !overriddenPackageAddons.removed.includes(key);
    });
    
    const complimentaryAddons = filteredAddons.filter(addon => !(addon.price && addon.price > 0));
    if (complimentaryAddons.length > 0 || overriddenPackageAddons.added.length > 0) {
      const packageAddons: SelectedAddOn[] = complimentaryAddons.map(addon => {
        const pricingType = addon.pricingType || 'fixed';
        const isStudioTime = addon.id === 'studio-time' || addon.name.toLowerCase().includes('studio time') || addon.name.toLowerCase().includes('studio');
        
        return {
          ...addon,
          quantity: pricingType === 'per-each' ? 1 : undefined,
          hours: pricingType === 'per-hour' 
            ? (isStudioTime && packageDurationHours ? packageDurationHours : 1)
            : undefined,
        };
      });
      
      // Add custom addons
      const customAddons: SelectedAddOn[] = overriddenPackageAddons.added.map(addon => ({
        ...addon,
        quantity: addon.pricingType === 'per-each' ? (addon.quantity || 1) : undefined,
        hours: addon.pricingType === 'per-hour' ? (addon.hours || 1) : undefined,
      }));
      
      setSelectedAddOns([...packageAddons, ...customAddons]);
    } else {
      setSelectedAddOns([]);
    }
    const packageCurrency = pkg.currency || 'R';
    if (packageCurrency === 'R' || packageCurrency === 'USD' || packageCurrency === 'EUR' || packageCurrency === 'GBP' || packageCurrency === 'CAD' || packageCurrency === 'AUD') {
      setSelectedCurrency(packageCurrency);
    }
    if (fromEnquiry) return;
    setCurrentStep(filterStepCount + 1); // Move to currency selection (after filters + package)
  };

  // Update studio time hours when the package changes. Restoring a saved quotation
  // must not rewrite its add-ons, or Save would look like an edit.
  useEffect(() => {
    if (skipAddonSync.current) {
      skipAddonSync.current = false;
      return;
    }
    if (selectedPackage?.durationMinutes) {
      const packageDurationHours = selectedPackage.durationMinutes / 60;
      
      setSelectedAddOns(prev => prev.map(addon => {
        // Check if this is studio time addon
        const isStudioTime = addon.id === 'studio-time' || addon.name.toLowerCase().includes('studio time') || addon.name.toLowerCase().includes('studio');
        
        // Update hours for studio time addons to match package duration
        if (isStudioTime && addon.pricingType === 'per-hour') {
          return { ...addon, hours: packageDurationHours };
        }
        return addon;
      }));
    }
  }, [selectedPackage]);

  const getClientDetailsStep = () => {
    // Client details step is always second to last (before review)
    return totalSteps - 2;
  };

  const handleCurrencySelect = (currency: 'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD') => {
    setSelectedCurrency(currency);
    const currencyStep = filterStepCount + 1; // After filters + package
    if (selectedPackage) {
      const packageAddons = getPackageAddons(selectedPackage);
      const additionalAddons = getAdditionalAddons(selectedPackage);
      if (packageAddons.length > 0 || additionalAddons.length > 0) {
        // Has addons, go to addons
        setCurrentStep(currencyStep + 1);
      } else {
        // No addons, go to client details
        setCurrentStep(getClientDetailsStep());
      }
    }
  };


  const handleAddOnToggle = (addon: AddOn) => {
    setSelectedAddOns(prev => {
      const isSelected = prev.some(a => a.id === addon.id || (a.id === undefined && a.name === addon.name));
      if (isSelected) {
        return prev.filter(a => a.id !== addon.id && !(a.id === undefined && a.name === addon.name));
      } else {
        // Check if this is studio time addon
        const isStudioTime = addon.id === 'studio-time' || addon.name.toLowerCase().includes('studio time') || addon.name.toLowerCase().includes('studio');
        
        // Calculate package duration in hours (exact decimal hours) if package is selected
        const packageDurationHours = selectedPackage?.durationMinutes 
          ? selectedPackage.durationMinutes / 60 
          : undefined;
        
        // Add with default quantity/hours based on pricing type
        // For studio time, use package duration if available
        const selectedAddon: SelectedAddOn = {
          ...addon,
          quantity: addon.pricingType === 'per-each' ? 1 : undefined,
          hours: addon.pricingType === 'per-hour' 
            ? (isStudioTime && packageDurationHours ? packageDurationHours : 1)
            : undefined,
        };
        return [...prev, selectedAddon];
      }
    });
  };

  const handleAddonQuantityChange = (addonId: string | undefined, addonName: string, value: number) => {
    setSelectedAddOns(prev => prev.map(addon => {
      if (addon.id === addonId || (addon.id === undefined && addon.name === addonName)) {
        return { ...addon, quantity: Math.max(1, value) };
      }
      return addon;
    }));
  };

  const handleAddonHoursChange = (addonId: string | undefined, addonName: string, value: number) => {
    setSelectedAddOns(prev => prev.map(addon => {
      if (addon.id === addonId || (addon.id === undefined && addon.name === addonName)) {
        return { ...addon, hours: Math.max(0.5, value) };
      }
      return addon;
    }));
  };

  const handleAddonPriceOverride = (addonId: string | undefined, addonName: string, newPriceInSelectedCurrency: number) => {
    const addonKey = addonId || addonName;
    if (!selectedAddOns.some(a => a.id === addonId || (a.id === undefined && a.name === addonName))) return;

    setOverriddenPackageAddons(prev => {
      const newMap = new Map(prev.priceOverrides);
      if (newPriceInSelectedCurrency >= 0) {
        newMap.set(addonKey, newPriceInSelectedCurrency);
      } else {
        newMap.delete(addonKey);
      }
      return { ...prev, priceOverrides: newMap };
    });

    setSelectedAddOns(prev => prev.map(a => {
      if (a.id === addonId || (a.id === undefined && a.name === addonName)) {
        return {
          ...a,
          priceOverridden: true,
          overridePrice: newPriceInSelectedCurrency,
          overrideCurrency: selectedCurrency,
        };
      }
      return a;
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setShowPreview(false);
      if (fromEnquiry && currentStep === totalSteps - 1) {
        setCurrentStep(0);
        return;
      }
      setCurrentStep(prev => prev - 1);
    }
  };

  /** Jump back to an earlier step from the review. The preview is hidden because it would be out of date after an edit. */
  const editStep = (step: number) => {
    setShowPreview(false);
    if (fromEnquiry && step < totalSteps - 1 && (step === getClientDetailsStep() || step <= filterStepCount)) {
      setCurrentStep(0);
      window.setTimeout(() => {
        document.querySelector('.enquiry-quote-setup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }
    setCurrentStep(step);
    window.setTimeout(() => {
      document.querySelector('.wizard-step-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const hasExtrasStep = Boolean(
    selectedPackage && (getPackageAddons(selectedPackage).length > 0 || getAdditionalAddons(selectedPackage).length > 0)
  );

  const effectiveSessions = (): QuoteSession[] => {
    if (sessions.length > 0) return sessions;
    if (!preferredDate && !preferredTime && !adhocPackage) return [];
    return [blankSession({
      id: 'session-1',
      date: preferredDate,
      startTime: preferredTime,
      durationMinutes: selectedPackage?.durationMinutes || 180,
      location: preferredLocation,
    })];
  };

  // The quotation itself carries the date, time and place. Messages only carry the private link.
  const shootForMessages = () => {
    const schedule = effectiveSessions();
    const first = schedule[0];
    return {
      date: first?.date || preferredDate,
      time: first?.startTime || preferredTime,
      location: first?.location || preferredLocation,
      durationMinutes: totalSessionMinutes(schedule) || selectedPackage?.durationMinutes || undefined,
      sessions: schedule,
    };
  };

  const documentDetails = () => ({
    clientName: clientName.trim() || undefined,
    personalMessage: personalMessage.trim() || undefined,
    contactEmail: contactInfo.email || '',
    contactPhone: contactInfo.phone || '',
    shoot: shootForMessages(),
  });

  const syncPreferredFromSessions = (nextSessions: QuoteSession[]) => {
    const preferred = preferredFromSessions(nextSessions);
    setPreferredDate(preferred.preferredDate);
    setPreferredTime(preferred.preferredTime);
    setPreferredLocation(normalizeShootLocation(preferred.preferredLocation));
  };

  const updateSession = (id: string, patch: Partial<QuoteSession>) => {
    setSessions((current) => {
      const next = current.map((session) => (session.id === id ? { ...session, ...patch } : session));
      syncPreferredFromSessions(next);
      return next;
    });
  };

  const addSession = () => {
    const inheritedLocation = placesArePerDay ? { ...EMPTY_SHOOT_LOCATION } : preferredLocation;
    setSessions((current) => {
      const next = current.length
        ? current.concat([blankSession({
            label: `Day ${current.length + 1}`,
            location: inheritedLocation,
            durationMinutes: 180,
            priceMajor: adhocPackage ? 0 : null,
          })])
        : [
            blankSession({
              id: 'session-1',
              label: 'Day 1',
              date: preferredDate,
              startTime: preferredTime,
              durationMinutes: selectedPackage?.durationMinutes || 180,
              location: inheritedLocation,
              priceMajor: adhocPackage ? 0 : null,
            }),
            blankSession({
              label: 'Day 2',
              location: inheritedLocation,
              durationMinutes: 180,
              priceMajor: adhocPackage ? 0 : null,
            }),
          ];
      syncPreferredFromSessions(next);
      return next;
    });
  };

  const choosePlacesMode = (perDay: boolean) => {
    setPlacesArePerDay(perDay);
    if (perDay) {
      const shared = normalizeShootLocation(preferredLocation);
      setSessions((current) => current.map((session) => (
        hasShootLocation(session.location) ? session : { ...session, location: shared }
      )));
      return;
    }
    const source = sessions.find((session) => hasShootLocation(session.location))?.location || preferredLocation;
    const place = normalizeShootLocation(source);
    setPreferredLocation(place);
    setSessions((current) => {
      const next = current.map((session) => ({ ...session, location: place }));
      return next;
    });
  };

  const removeSession = (id: string) => {
    setSessions((current) => {
      if (current.length <= 1) {
        const next = current.map((session) => (session.id === id
          ? blankSession({ id: session.id, label: session.label, location: preferredLocation, priceMajor: adhocPackage ? 0 : null })
          : session));
        syncPreferredFromSessions(next);
        return next;
      }
      const next = current.filter((session) => session.id !== id);
      syncPreferredFromSessions(next);
      return next;
    });
  };

  const startAdhocPackage = () => {
    if (!fromEnquiry && currentStep < filterStepCount) {
      setCurrentStep(filterStepCount);
    }
    setBuildingAdhoc(true);
    setAdhocDraft({
      name: adhocPackage?.name || '',
      description: adhocPackage?.description || '',
      features: (adhocPackage?.features || []).join('\n'),
    });
  };

  const confirmAdhocPackage = () => {
    const name = adhocDraft.name.trim();
    if (!name) {
      setSaveMessage('Give the ad hoc package a name.');
      return;
    }
    const features = adhocDraft.features
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 12);
    const created: AdhocPackage = {
      name,
      description: adhocDraft.description.trim() || undefined,
      features: features.length ? features : undefined,
      currency: selectedCurrency,
    };
    const nextSessions = sessions.length > 0
      ? sessions.map((session) => ({
          ...session,
          priceMajor: session.priceMajor == null ? 0 : session.priceMajor,
        }))
      : [
          blankSession({
            id: 'session-1',
            label: 'Day 1',
            date: preferredDate,
            startTime: preferredTime,
            durationMinutes: 180,
            location: preferredLocation,
            priceMajor: 0,
          }),
        ];
    setAdhocPackage(created);
    setSessions(nextSessions);
    syncPreferredFromSessions(nextSessions);
    setSelectedPackage(makeAdhocPackageShell(created, nextSessions));
    setSelectedAddOns([]);
    setBuildingAdhoc(false);
    setSaveMessage('');
    if (!fromEnquiry) setCurrentStep(filterStepCount + 1);
  };

  const generateQuote = async () => {
    if (!selectedPackage) return;
    const snapshot = await buildSnapshot();
    if (!snapshot) return;
    setPricingSnapshot(snapshot);
    setQuoteHTML(renderQuoteHtml(snapshot, documentDetails()));
    setShowPreview(true);
  };

  const handleSendEmail = async () => {
    if (!selectedPackage || !recipientEmail.trim() || !clientLink) {
      setSaveMessage(clientLink ? 'Enter the recipient email address.' : 'Issue the quotation first. The email then includes the client\'s private link.');
      return;
    }
    const snapshot = pricingSnapshot || await buildSnapshot();
    if (!snapshot) return;
    window.location.href = quoteMailto(snapshot, recipientEmail.trim(), {
      ...documentDetails(),
      clientLink,
    });
  };

  const persistQuote = async (issue: boolean): Promise<StoredQuote | null> => {
    if (!selectedPackage || saving) return null;
    setSaving(true);
    try {
      const snapshot = await buildSnapshot();
      if (!snapshot) throw new Error('Choose a package first.');
      if (adhocPackage && sessions.every((session) => !(Number(session.priceMajor) > 0))) {
        throw new Error('Enter a price for at least one session on the ad hoc package.');
      }
      setPricingSnapshot(snapshot);
      const schedule = effectiveSessions();
      const preferred = preferredFromSessions(schedule);
      const payload = {
        client: {
          name: clientName.trim(),
          email: recipientEmail.trim(),
          company: companyName.trim(),
          billingAddress: billingAddress.trim(),
          countryCode,
          phone: clientPhone.trim(),
          preference: contactPreference,
          message: personalMessage.trim(),
        },
        shootType: filterAnswers.category && filterAnswers.category !== 'any' ? filterAnswers.category : '',
        preferredDate: preferred.preferredDate || preferredDate,
        preferredTime: preferred.preferredTime || preferredTime,
        preferredLocation: preferred.preferredLocation.name || preferred.preferredLocation.address || preferred.preferredLocation.mapsUrl
          ? preferred.preferredLocation
          : preferredLocation,
        sessions: schedule,
        adhocPackage: adhocPackage || null,
        guideIds: selectedGuideIds,
        snapshot,
        draft: {
          packageId: selectedPackage.id,
          currency: selectedCurrency,
          discountPercent: discount,
          depositPercent,
          overridePackagePrice,
          overridePackageCurrency,
          selectedAddOns,
          customAddons: overriddenPackageAddons.added,
          clientName,
          personalMessage,
          recipientEmail,
          clientPhone,
          countryCode,
          contactPreference,
          companyName,
          billingAddress,
          preferredDate: preferred.preferredDate || preferredDate,
          preferredTime: preferred.preferredTime || preferredTime,
          preferredLocation: preferred.preferredLocation.name || preferred.preferredLocation.address || preferred.preferredLocation.mapsUrl
            ? preferred.preferredLocation
            : preferredLocation,
          sessions: schedule,
          adhocPackage: adhocPackage || null,
          shootType: filterAnswers.category && filterAnswers.category !== 'any' ? filterAnswers.category : '',
        },
      };
      const base = `${API_BASE_URL}/quotes`;
      const liveEdit = Boolean(initialDraft?.id && (initialDraft.status === 'issued' || initialDraft.status === 'accepted'));
      if (liveEdit && initialDraft?.id) {
        if (issuedBaseline.current && editSignature(payload) === issuedBaseline.current) {
          setSaveMessage(`${initialDraft.quoteNumber} is unchanged and stays ${initialDraft.status}.`);
          return initialDraft;
        }
        const written = await fetch(`${base}/${initialDraft.id}/details`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!written.ok) {
          const body = await written.json().catch(() => ({}));
          throw new Error(body.error || 'The quotation could not be saved.');
        }
        const saved = await written.json();
        setSaveMessage(`Saved changes to ${saved.quoteNumber}. It stays ${saved.status}.`);
        if (saved.clientToken) setClientLink(`${window.location.origin}/quote/${saved.clientToken}`);
        return saved as StoredQuote;
      }
      if (issuedSourceId.current && !savedQuoteId) {
        if (issuedBaseline.current && editSignature(payload) === issuedBaseline.current) {
          setSaveMessage(`${initialDraft?.quoteNumber || 'This quotation'} is unchanged and stays issued.`);
          return initialDraft || null;
        }
        const revisedResponse = await fetch(`${base}/${issuedSourceId.current}/revise`, { method: 'POST' });
        const revisedBody = await revisedResponse.json().catch(() => ({}));
        if (!revisedResponse.ok) throw new Error(revisedBody.error || 'The changes could not be saved as a new version.');
        const written = await fetch(`${base}/${revisedBody.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!written.ok) {
          const body = await written.json().catch(() => ({}));
          throw new Error(body.error || 'The new version was opened but the changes could not be saved.');
        }
        issuedSourceId.current = '';
        issuedBaseline.current = '';
        let saved = await written.json();
        setSavedQuoteId(saved.id);
        if (issue) {
          const issued = await fetch(`${base}/${saved.id}/issue`, { method: 'POST' });
          if (!issued.ok) {
            const body = await issued.json().catch(() => ({}));
            throw new Error(`${saved.quoteNumber} is saved as a draft but was not issued: ${body.error || 'the server refused it.'}`);
          }
          saved = await issued.json();
        }
        setSaveMessage(issue ? `Saved changes and issued ${saved.quoteNumber}.` : `Saved changes to ${saved.quoteNumber}.`);
        if (issue && saved.clientToken) setClientLink(`${window.location.origin}/quote/${saved.clientToken}`);
        return saved as StoredQuote;
      }
      const response = await fetch(savedQuoteId ? `${base}/${savedQuoteId}` : base, {
        method: savedQuoteId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'The quotation could not be saved.');
      }
      let saved = await response.json();
      // Remember the draft before issuing, so a failed issue is retried on the same quotation.
      setSavedQuoteId(saved.id);
      if (issue) {
        const issued = await fetch(`${base}/${saved.id}/issue`, { method: 'POST' });
        if (!issued.ok) {
          const body = await issued.json().catch(() => ({}));
          throw new Error(`${saved.quoteNumber} is saved as a draft but was not issued: ${body.error || 'the server refused it.'}`);
        }
        saved = await issued.json();
      }
      setSaveMessage(issue ? `Issued ${saved.quoteNumber}.` : `Saved draft ${saved.quoteNumber}.`);
      try {
        const pendingRaw = sessionStorage.getItem('studioPendingEnquiryLink');
        if (pendingRaw && saved.id) {
          const pending = JSON.parse(pendingRaw) as { enquiryId?: string };
          if (pending.enquiryId) {
            await fetch(`${API_BASE_URL}/studio/enquiries/${pending.enquiryId}/link-quote`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quoteId: saved.id }),
            });
          }
          sessionStorage.removeItem('studioPendingEnquiryLink');
        }
      } catch (_) {
        // Linking is best-effort; the draft itself is already saved.
      }
      if (issue && saved.clientToken) {
        setClientLink(`${window.location.origin}/quote/${saved.clientToken}`);
      }
      return saved as StoredQuote;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const saved = await persistQuote(false);
      if (!saved) return;
      if (initialDraft?.status === 'issued' && saved.id === initialDraft.id) {
        if (onDone) window.setTimeout(() => onDone(saved), 500);
        return;
      }
      setSaveMessage((previous) => `${previous || 'Draft saved.'} Returning to the shoot.`);
      if (onDone) window.setTimeout(() => onDone(saved || undefined), 400);
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'The quotation could not be saved.');
    }
  };

  const handleSaveChanges = async () => {
    const problem = deliveryContactProblem();
    if (problem) {
      setContactError(problem);
      setCurrentStep(getClientDetailsStep());
      return;
    }
    setContactError('');
    try {
      const saved = await persistQuote(true);
      if (!saved) return;
      if (saved.id === initialDraft?.id) {
        if (onDone) window.setTimeout(() => onDone(saved), 500);
        return;
      }
      try {
        await sendIssuedNotice(saved.id);
      } catch (_) {
        setSaveMessage(`Saved changes and issued ${saved.quoteNumber}. The email could not be queued.`);
      }
      setSaveMessage((previous) => `${previous} Returning to the shoot.`);
      if (onDone) window.setTimeout(() => onDone(saved), 500);
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'The quotation could not be saved.');
    }
  };

  const deliveryContactProblem = () => {
    if (!clientName.trim()) return 'A client name is needed before this quotation is issued.';
    const email = recipientEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'The email address is not valid.';
    const phoneDigits = clientPhone.trim().startsWith('+')
      ? clientPhone.replace(/\D/g, '')
      : `${countryCode.replace(/\D/g, '')}${clientPhone.replace(/\D/g, '').replace(/^0+/, '')}`;
    if (clientPhone.trim() && (phoneDigits.length < 8 || phoneDigits.length > 15)) return 'The mobile number is not valid.';
    if (contactPreference === 'email' && !email) return 'An email address is needed for this client.';
    if (contactPreference === 'whatsapp' && !clientPhone.trim()) return 'A mobile number is needed for this client.';
    if (!email && !clientPhone.trim()) return 'Add an email address or a mobile number before issuing this quotation.';
    return '';
  };

  const editingIssuedQuote = initialDraft?.status === 'issued' || initialDraft?.status === 'accepted';
  const isIssued = !editingIssuedQuote && (Boolean(clientLink) || Boolean(initialDraft?.status && initialDraft.status !== 'draft'));

  const handleIssueQuote = async () => {
    if (isIssued) return;
    const problem = deliveryContactProblem();
    if (problem) {
      setContactError(problem);
      setCurrentStep(getClientDetailsStep());
      window.setTimeout(() => {
        document.querySelector('[data-testid="contact-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    setContactError('');
    let saved: StoredQuote | null = null;
    try {
      saved = await persistQuote(true);
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'The quotation could not be issued.');
      return;
    }
    if (!saved?.id) return;
    const text = `This is your quotation ${saved.quoteNumber}.`;
    let sent: SentReply;
    try {
      const notice = await sendIssuedNotice(saved.id);
      sent = { text, ...notice };
    } catch (error) {
      sent = { text, delivery: 'failed', destination: recipientEmail.trim() };
    }
    setIssuedReach({ quoteId: saved.id, sent });
    window.setTimeout(() => issuedReachRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  };

  const sendIssuedNotice = async (quoteId: string): Promise<Omit<SentReply, 'text'>> => {
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${quoteId}/notices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'quotation-issued', send: true }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'The quotation email could not be queued.');
    return {
      delivery: body.status as string | undefined,
      destination: body.destination as string | undefined,
      providerAccepted: Boolean(body.providerAccepted),
    };
  };

  const recordIssuedHandoff = async (quoteId: string, text: string) => {
    await fetch(`${API_BASE_URL}/studio/messages/${quoteId}/note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, channel: 'whatsapp-note' }),
    });
  };

  const clientPhoneE164 = () => {
    const digits = clientPhone.replace(/\D/g, '');
    if (!digits) return '';
    return clientPhone.trim().startsWith('+')
      ? `+${digits}`
      : `+${countryCode.replace(/\D/g, '')}${digits.replace(/^0+/, '')}`;
  };

  const handleCopyQuote = () => {
    if (!quoteHTML) {
      generateQuote();
      return;
    }

    if (navigator.clipboard && window.ClipboardItem) {
      const blob = new Blob([quoteHTML], { type: 'text/html' });
      const data = [new ClipboardItem({ 'text/html': blob })];
      
      navigator.clipboard.write(data).then(() => {
        alert('Quote copied to clipboard! You can paste it into an email or document.');
      }).catch(() => {
        copyToClipboardFallback();
      });
    } else {
      copyToClipboardFallback();
    }
  };

  const copyToClipboardFallback = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = quoteHTML;
    tempDiv.style.position = 'fixed';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);
    
    const range = document.createRange();
    range.selectNodeContents(tempDiv);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    try {
      document.execCommand('copy');
      alert('Quote copied to clipboard! You can paste it into an email or document.');
    } catch (err) {
      alert('Unable to copy. Please use the Print option or manually copy the preview.');
    }
    
    document.body.removeChild(tempDiv);
    if (selection) {
      selection.removeAllRanges();
    }
  };

  const handleDownloadPdf = async () => {
    const snapshot = pricingSnapshot || await buildSnapshot();
    if (!snapshot) {
      setPdfNote('Prepare the quotation before downloading the PDF.');
      return;
    }
    setPdfBusy(true);
    setPdfNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/quotes/preview-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quoteNumber: initialDraft?.quoteNumber || '',
          version: initialDraft?.version,
          clientName: clientName.trim(),
          email: recipientEmail.trim(),
          phone: clientPhoneE164(),
          company: companyName.trim(),
          billingAddress: billingAddress.trim(),
          personalMessage: personalMessage.trim(),
          shootType: filterAnswers.category && filterAnswers.category !== 'any' ? filterAnswers.category : '',
          contactEmail: contactInfo.email || '',
          contactPhone: contactInfo.phone || '',
          shoot: shootForMessages(),
          snapshot,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setPdfNote(body.error || 'The PDF could not be prepared.');
        return;
      }
      await downloadResponseFile(response, 'Carmen-Lorraine-quotation.pdf');
    } catch (error) {
      setPdfNote('The PDF could not be prepared.');
    } finally {
      setPdfBusy(false);
    }
  };

  const handlePrintQuote = () => {
    if (!quoteHTML) {
      generateQuote();
      return;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(quoteHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const placeFilled = Boolean(preferredLocation.name || preferredLocation.address || preferredLocation.mapsUrl);
  const quotationProgress = {
    savedForClient: Boolean(clientLink || issuedReach),
    openedExisting: Boolean(initialDraft),
    showPreview,
    hasPackage: Boolean(selectedPackage),
    step: currentStep,
    hasClientDetails: Boolean(
      clientName.trim()
      || personalMessage.trim()
      || recipientEmail.trim()
      || clientPhone.trim()
      || companyName.trim()
      || billingAddress.trim(),
    ),
    hasSchedule: Boolean(preferredDate || preferredTime || placeFilled || sessions.length),
    hasExtras: Boolean(
      selectedAddOns.length
      || discount > 0
      || overridePackagePrice != null
      || selectedGuideIds.length
      || adhocPackage,
    ),
    hasCategory: Boolean(filterAnswers.category),
  };
  const unsavedQuotation = quotationWouldBeLost(quotationProgress);
  const unsavedRef = useRef(unsavedQuotation);
  unsavedRef.current = unsavedQuotation;

  const confirmLeaveQuotation = () => (
    unsavedRef.current ? window.confirm(LEAVE_QUOTATION_MESSAGE) : true
  );
  const confirmLeaveRef = useRef(confirmLeaveQuotation);
  confirmLeaveRef.current = confirmLeaveQuotation;

  useEffect(() => {
    if (!onLeaveGuard) return undefined;
    onLeaveGuard(() => confirmLeaveRef.current());
    return () => onLeaveGuard(null);
  }, [onLeaveGuard]);

  useEffect(() => {
    if (!unsavedQuotation) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [unsavedQuotation]);

  const resetQuote = () => {
    const hasEnteredQuotation = quotationWouldBeLost({ ...quotationProgress, savedForClient: false });
    if (hasEnteredQuotation && !window.confirm(START_OVER_QUOTATION_MESSAGE)) return;
    setSelectedPackage(null);
    setSelectedAddOns([]);
    setSelectedCurrency('R');
    setTotalPrice(0);
    setCurrentStep(0);
    setShowPreview(false);
    setQuoteHTML('');
    setRecipientEmail('');
    setClientName('');
    setPersonalMessage('');
    setPreferredDate('');
    setPreferredTime('');
    setPreferredLocation({ ...EMPTY_SHOOT_LOCATION });
    setFilterAnswers({});
    setPackages(allPackages);
    setDiscount(0);
    setDepositPercent(50);
    setSubtotal(0);
    setPricingSnapshot(null);
    setSavedQuoteId(null);
    setSaveMessage('');
    setClientLink('');
    setIssuedReach(null);
    setOverridePackagePrice(null);
    setOverridePackageCurrency(null);
    setOverriddenPackageAddons({ removed: [], added: [], priceOverrides: new Map() });
    setAddonPriceInputs(new Map());
    setDisplayBasePrice(0);
  };

  const formatDuration = (minutes: number): string => {
    const hours = minutes / 60;
    if (hours === 0.5) return '½ hour';
    if (hours === 1.5) return '1½ hours';
    if (hours % 1 === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours.toFixed(1)} hours`;
  };

  const chooseQuotation = (name: string) => {
    setFilterAnswers({ category: name });
    if (selectedPackage && name !== 'any' && !packageMatchesShootType(selectedPackage, name)) {
      setSelectedPackage(null);
    }
  };

  const openPackageSetup = () => {
    const chosen = filterAnswers.category && filterAnswers.category !== 'any' ? filterAnswers.category : '';
    setPackageSetupError('');
    setPackageDraft({
      name: '',
      category: chosen,
      price: '',
      durationMinutes: '',
      short: '',
      features: '',
      showOnWebsite: false,
    });
    setSettingUpPackage(true);
  };

  const saveQuotePackage = async () => {
    const name = packageDraft.name.trim();
    const category = packageDraft.category.trim();
    const price = Number(packageDraft.price);
    if (!name || !category) {
      setPackageSetupError('Add a name and what this quotation is for.');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setPackageSetupError('Add the price in rand.');
      return;
    }
    const minutes = Math.round(Number(packageDraft.durationMinutes));
    const features = packageDraft.features.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    setSavingPackage(true);
    setPackageSetupError('');
    try {
      const existing = await loadServicesFromServer();
      const baseSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'package';
      const slugTaken = existing.some((pkg) => (pkg.slug || '').toLowerCase() === baseSlug);
      const created: Package = {
        id: `pkg-${Date.now()}`,
        name,
        slug: slugTaken ? `${baseSlug}-${Date.now()}` : baseSlug,
        category,
        short: packageDraft.short.trim() || undefined,
        price,
        currency: 'R',
        durationMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : undefined,
        features,
        available: packageDraft.showOnWebsite,
        forQuoting: true,
        featured: false,
        order: 999,
      };
      const saved = await saveServicesToServer([...existing, created]);
      if (!saved) {
        setPackageSetupError('The package could not be saved. Try again.');
        return;
      }
      const quotable = [...existing, created].filter(packageIsQuotable);
      setAllPackages(quotable);
      setFilterAnswers({ category });
      setSettingUpPackage(false);
      handlePackageSelect(created);
    } catch (error) {
      console.error('Error saving quote package:', error);
      setPackageSetupError('The package could not be saved. Try again.');
    } finally {
      setSavingPackage(false);
    }
  };

  const renderPackageSetup = () => (
    <div className="quote-package-setup" data-testid="quote-package-setup">
      {settingUpPackage ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            saveQuotePackage();
          }}
        >
          <h3>Set up a package</h3>
          <p className="section-lead">This is what the quotation starts from. It stays off the website unless you choose to show it.</p>
          {packageSetupError ? <p className="studio-error" role="alert">{packageSetupError}</p> : null}
          <label>
            Package name
            <input
              value={packageDraft.name}
              onChange={(event) => setPackageDraft((current) => ({ ...current, name: event.target.value }))}
              required
              data-testid="quote-package-name"
            />
          </label>
          <label>
            What this quotation is for
            <input
              value={packageDraft.category}
              onChange={(event) => setPackageDraft((current) => ({ ...current, category: event.target.value }))}
              placeholder="Portrait, Wedding, Family"
              required
              data-testid="quote-package-category"
            />
          </label>
          <label>
            Price (rand)
            <input
              inputMode="decimal"
              value={packageDraft.price}
              onChange={(event) => setPackageDraft((current) => ({ ...current, price: event.target.value }))}
              placeholder="4500"
              required
              data-testid="quote-package-price"
            />
          </label>
          <label>
            Duration (minutes)
            <input
              inputMode="numeric"
              value={packageDraft.durationMinutes}
              onChange={(event) => setPackageDraft((current) => ({ ...current, durationMinutes: event.target.value }))}
              placeholder="60"
            />
          </label>
          <label>
            Short description
            <input
              value={packageDraft.short}
              onChange={(event) => setPackageDraft((current) => ({ ...current, short: event.target.value }))}
            />
          </label>
          <label>
            What’s included
            <textarea
              rows={4}
              value={packageDraft.features}
              onChange={(event) => setPackageDraft((current) => ({ ...current, features: event.target.value }))}
              placeholder={'One inclusion per line'}
            />
          </label>
          <label className="setup-check">
            <input
              type="checkbox"
              checked={packageDraft.showOnWebsite}
              onChange={(event) => setPackageDraft((current) => ({ ...current, showOnWebsite: event.target.checked }))}
            />
            <span>Also show this package on the website</span>
          </label>
          <div className="setup-actions">
            <button type="submit" className="btn-primary" disabled={savingPackage} data-testid="quote-package-save">
              {savingPackage ? 'Saving…' : 'Save and quote from this'}
            </button>
            <button type="button" className="studio-secondary" onClick={() => setSettingUpPackage(false)} disabled={savingPackage}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="studio-secondary" onClick={openPackageSetup} data-testid="setup-quote-package">
          Set up a package
        </button>
      )}
    </div>
  );

  const onEnquiryPage = fromEnquiry && currentStep < totalSteps - 1;

  const renderSessionsEditor = (options: { showPrices: boolean; title?: string }) => {
    const rows = sessions.length > 0
      ? sessions
      : [blankSession({
          id: 'session-1',
          date: preferredDate,
          startTime: preferredTime,
          durationMinutes: selectedPackage?.durationMinutes || 180,
          location: placesArePerDay ? { ...EMPTY_SHOOT_LOCATION } : preferredLocation,
          priceMajor: options.showPrices ? 0 : null,
        })];
    const ensureRows = () => {
      if (sessions.length === 0) {
        setSessions(rows);
        syncPreferredFromSessions(rows);
      }
    };
    const addDay = () => {
      ensureRows();
      scrollToNewDay.current = true;
      addSession();
    };
    return (
      <div className="quote-sessions" data-testid="quote-sessions" ref={sessionsRootRef}>
        <div className="quote-sessions-header">
          <div>
            <h4>{options.title || 'Sessions'}</h4>
            <p className="form-help quote-sessions-lead">
              One card per day. Set hours
              {options.showPrices ? ', a price, and a short description' : ''}
              {' '}for each, then add another day below.
            </p>
          </div>
        </div>
        <fieldset className="preferred-date quote-places-choice">
          <legend>Places</legend>
          <div className="preferred-date-choice" role="radiogroup" aria-label="Places">
            <button
              type="button"
              role="radio"
              aria-checked={!placesArePerDay}
              className={!placesArePerDay ? 'is-selected' : ''}
              onClick={() => choosePlacesMode(false)}
              data-testid="places-one-for-all"
            >
              One place for all days
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={placesArePerDay}
              className={placesArePerDay ? 'is-selected' : ''}
              onClick={() => choosePlacesMode(true)}
              data-testid="places-per-day"
            >
              Each day has its own place
            </button>
          </div>
        </fieldset>
        {placesArePerDay ? null : (
          <ShootLocationField
            value={preferredLocation}
            onChange={(value) => {
              setPreferredLocation(value);
              setSessions((current) => (
                current.length
                  ? current.map((session) => ({ ...session, location: value }))
                  : current
              ));
            }}
            label="Place for every day"
            placeLabel="Client has a place"
            laterLabel="Decide later"
            help="This place is used for every day. Choose each day having its own place when the days are in different locations."
          />
        )}
        {rows.map((session, index) => {
          const dayTitle = session.label?.trim() || `Day ${index + 1}`;
          const canRemove = !(rows.length <= 1 && !options.showPrices);
          return (
            <article key={session.id} className="quote-session-row" data-testid={`quote-session-${index}`}>
              <header className="quote-session-row-header">
                <div className="quote-session-day-mark" aria-hidden="true">{index + 1}</div>
                <label className="quote-session-label">
                  <input
                    value={session.label || ''}
                    onChange={(event) => {
                      ensureRows();
                      updateSession(session.id, { label: event.target.value });
                    }}
                    placeholder={`Day ${index + 1}`}
                    aria-label={`Label for day ${index + 1}`}
                  />
                </label>
                {canRemove ? (
                  <button
                    type="button"
                    className="quote-session-remove"
                    onClick={() => {
                      ensureRows();
                      removeSession(session.id);
                    }}
                    aria-label={`Remove ${dayTitle}`}
                  >
                    Remove
                  </button>
                ) : null}
              </header>

              <div className="quote-session-block">
                <p className="quote-session-block-title">When</p>
                <PreferredDateField
                  value={session.date || ''}
                  onChange={(value) => {
                    ensureRows();
                    updateSession(session.id, { date: value });
                  }}
                  label="Date"
                  dateLabel="Pick date"
                  laterLabel="TBD"
                  layout="inline"
                  density="compact"
                />
                <div className={`quote-session-schedule${options.showPrices ? ' has-price' : ''}`}>
                  <label>
                    Start
                    <select
                      value={session.startTime || ''}
                      onChange={(event) => {
                        ensureRows();
                        updateSession(session.id, { startTime: event.target.value });
                      }}
                    >
                      <option value="">To be agreed</option>
                      {HALF_HOUR_TIMES.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Duration
                    <select
                      value={String(session.durationMinutes || 180)}
                      onChange={(event) => {
                        ensureRows();
                        updateSession(session.id, { durationMinutes: Number(event.target.value) });
                      }}
                    >
                      {DURATION_OPTIONS_MINUTES.map((minutes) => (
                        <option key={minutes} value={minutes}>{formatDurationLabel(minutes)}</option>
                      ))}
                    </select>
                  </label>
                  {options.showPrices ? (
                    <label>
                      Price ({selectedCurrency})
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={session.priceMajor == null ? '' : String(session.priceMajor)}
                        onChange={(event) => {
                          ensureRows();
                          const raw = event.target.value;
                          updateSession(session.id, { priceMajor: raw === '' ? 0 : Number(raw) });
                        }}
                        data-testid={`session-price-${index}`}
                      />
                    </label>
                  ) : null}
                </div>
                {options.showPrices ? (
                  <label className="quote-session-description">
                    Short description
                    <input
                      value={session.notes || ''}
                      maxLength={500}
                      onChange={(event) => {
                        ensureRows();
                        updateSession(session.id, { notes: event.target.value });
                      }}
                      placeholder="What this day covers"
                      aria-label={`Short description for day ${index + 1}`}
                      data-testid={`session-description-${index}`}
                    />
                  </label>
                ) : null}
              </div>

              {placesArePerDay ? (
                <div className="quote-session-block">
                  <p className="quote-session-block-title">Where</p>
                  <ShootLocationField
                    value={normalizeShootLocation(session.location)}
                    onChange={(value) => {
                      ensureRows();
                      updateSession(session.id, { location: value });
                    }}
                    label={`Place for ${dayTitle}`}
                    placeLabel="Has a place"
                    laterLabel="Decide later"
                    density="compact"
                    openPlaceFields
                  />
                </div>
              ) : null}
            </article>
          );
        })}
        <button type="button" className="studio-secondary quote-sessions-add" onClick={addDay} data-testid="add-quote-session">
          Add another day
        </button>
        {rows.length > 1 ? (
          <p className="quote-sessions-summary" data-testid="sessions-total-hours">
            Total coverage: {formatDurationLabel(totalSessionMinutes(rows))}
            {options.showPrices
              ? ` · Session subtotal ${formatCurrency(
                  rows.reduce((sum, session) => sum + (Number(session.priceMajor) > 0 ? Number(session.priceMajor) : 0), 0),
                  selectedCurrency,
                )}`
              : ''}
          </p>
        ) : null}
      </div>
    );
  };

  const renderPackageCards = () => (
    <div className="packages-grid">
      <button
        type="button"
        className={`package-card adhoc-package-card ${adhocPackage && selectedPackage?.id === ADHOC_PACKAGE_ID ? 'selected' : ''}`}
        onClick={startAdhocPackage}
        data-testid="create-adhoc-package"
      >
        <div className="package-card-header">
          <h4>Create an ad hoc package</h4>
        </div>
        <div className="package-card-content">
          <p className="package-short">
            One-off coverage that is not in the catalogue — for multi-day events with different hours each day.
          </p>
        </div>
      </button>
      {buildingAdhoc ? (
        <form
          className="quote-package-setup adhoc-setup"
          data-testid="adhoc-package-form"
          onSubmit={(event) => {
            event.preventDefault();
            confirmAdhocPackage();
          }}
        >
          <h4>Ad hoc package</h4>
          <p className="form-help">This stays on this quotation only. It is never saved to the catalogue.</p>
          <label>
            Name
            <input
              value={adhocDraft.name}
              onChange={(event) => setAdhocDraft((current) => ({ ...current, name: event.target.value }))}
              placeholder="Two-day event coverage"
              required
            />
          </label>
          <label>
            Short description
            <input
              value={adhocDraft.description}
              onChange={(event) => setAdhocDraft((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
          <label>
            What’s included
            <textarea
              rows={4}
              value={adhocDraft.features}
              onChange={(event) => setAdhocDraft((current) => ({ ...current, features: event.target.value }))}
              placeholder={'One inclusion per line'}
            />
          </label>
          <div className="setup-actions">
            <button type="submit" className="btn-primary" data-testid="adhoc-package-save">
              Use this ad hoc package
            </button>
            <button type="button" className="studio-secondary" onClick={() => setBuildingAdhoc(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
      {packages.length === 0 && !adhocPackage ? (
        <p className="studio-empty">No catalogue packages for this quotation yet. Create an ad hoc package above, or set up a catalogue package.</p>
      ) : packages.map((pkg) => (
        <button
          key={pkg.id}
          type="button"
          className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
          onClick={() => handlePackageSelect(pkg)}
          data-testid={`package-choice-${pkg.id}`}
        >
          <div className="package-card-header">
            <h4>{pkg.name}</h4>
            {pkg.price ? (
              <div className="package-pricing-info">
                <div className="package-price">
                  <span className="price-label">From</span>
                  <span className="price-value">{formatCurrency(pkg.specialPrice || pkg.price, pkg.currency || 'R')}</span>
                </div>
              </div>
            ) : null}
          </div>
          <div className="package-card-content">
            {pkg.short ? <p className="package-short">{pkg.short}</p> : null}
            {pkg.durationMinutes ? (
              <div className="package-details">
                <div className="package-detail">
                  <i className="fas fa-clock" aria-hidden="true"></i>
                  <span>{formatDuration(pkg.durationMinutes)}</span>
                </div>
              </div>
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  const getStepTitle = () => {
    // Filter steps
    if (currentStep < filterStepCount) {
      return filterQuestions[currentStep].title;
    }
    // Package selection
    if (currentStep === filterStepCount) return 'Select Package';
    // Currency
    if (currentStep === filterStepCount + 1) return 'Select Currency';
    // Tier
    const currencyStep = filterStepCount + 1;
    // Add-ons
    if (selectedPackage) {
      const packageAddons = getPackageAddons(selectedPackage);
      const additionalAddons = getAdditionalAddons(selectedPackage);
      if (currentStep === currencyStep + 1 && (packageAddons.length > 0 || additionalAddons.length > 0)) {
        return 'Package Add-ons & Extras';
      }
    }
    // Client details
    if (currentStep === getClientDetailsStep()) return 'Client details';
    return 'Review';
  };

  const getCurrentFilterStep = () => {
    return currentStep < filterStepCount ? currentStep : -1;
  };

  const canProceedFromFilters = () => {
    if (getCurrentFilterStep() < 0) return false;
    const question = filterQuestions[getCurrentFilterStep()];
    return !!filterAnswers[question.id as keyof FilterAnswers];
  };

  return (
    <div className="quote-builder-wizard" data-testid="quote-builder">
      <div className="quote-builder-header">
        <div className="quote-builder-header-bar">
          <div className="quote-builder-heading">
            <h2>{fromEnquiry ? 'Quotation from this enquiry' : 'Quote Builder'}</h2>
            {onEnquiryPage ? null : (
              <div className="wizard-step-header">
                <h3 className="wizard-step-title">{getStepTitle()}</h3>
                <p className="wizard-step-subtitle">Step {currentStep + 1} of {totalSteps}</p>
              </div>
            )}
            {fromEnquiry ? (
              <p className="quote-builder-lead">
                {`Choose the quotation and a package for ${initialDraft?.client?.name || clientName || 'this client'}, then fill in the details.`}
              </p>
            ) : initialDraft?.status === 'draft' ? (
              <p className="quote-builder-lead">
                {`Editing ${initialDraft.quoteNumber} for ${initialDraft.client?.name || clientName || 'this client'}`}
              </p>
            ) : null}
            {saveMessage ? <p className="studio-note" data-testid="builder-context">{saveMessage}</p> : null}
            {catalogueWarnings.length > 0 ? (
              <div className="studio-error" role="alert" data-testid="catalogue-warnings">
                {catalogueWarnings.map((warning) => (
                  <p key={warning}>{warning}</p>
                ))}
              </div>
            ) : null}
          </div>
          {onDone ? (
            <button
              type="button"
              className="studio-secondary"
              onClick={() => {
                if (!confirmLeaveQuotation()) return;
                onDone();
              }}
              data-testid="builder-back"
            >
              {returnLabel}
            </button>
          ) : null}
        </div>
      </div>

      {/* Progress Bar */}
      {onEnquiryPage ? null : (
        <div className="wizard-progress">
          <div
            className="wizard-progress-bar"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          ></div>
        </div>
      )}

      {/* Step Content */}
      <div className="wizard-step-content">
        {onEnquiryPage ? (
          <div className="enquiry-quote-setup" data-testid="enquiry-quote-setup">
            <section>
              <h3>Quotation</h3>
              <p className="section-lead">Choose what this quotation is for. Packages for that choice appear next.</p>
              {!packagesLoaded ? (
                <p className="studio-empty">Loading the quotation list…</p>
              ) : catalogueCategories.length === 0 ? (
                <p className="studio-empty">No packages to quote from yet. Set one up below.</p>
              ) : (
                <div className="enquiry-quote-choices" role="group" aria-label="Quotation">
                  {catalogueCategories.map((name) => (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={filterAnswers.category === name}
                      onClick={() => chooseQuotation(name)}
                      data-testid={`quote-choice-${name}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </section>
            <section>
              <h3>Package</h3>
              {filterAnswers.category ? (
                <>
                  <p className="section-lead">Choose one package, or set up a new one to quote from. The details below are filled from the enquiry and can be changed.</p>
                  <div data-testid="enquiry-package-list">{renderPackageCards()}</div>
                </>
              ) : (
                <p className="studio-empty">Choose a quotation, or set up a package for a new one.</p>
              )}
              {renderPackageSetup()}
            </section>
          </div>
        ) : null}
        {/* Filter Steps */}
        {!onEnquiryPage && currentStep < filterStepCount && (
          <div className="filter-step">
            {(() => {
              const question = filterQuestions[currentStep];
              return (
                <div className="question-container">
                  <div className="question-header">
                    <h3 className="question-title">{question.title}</h3>
                  </div>
                  <div className="question-options">
                    {question.options.length === 0 ? (
                      <p className="studio-empty">No packages to quote from yet. Set one up below.</p>
                    ) : question.options.map((option: any) => {
                      const isSelected = filterAnswers[question.id as keyof FilterAnswers] === option.id;
                      const hasColor = 'color' in option && option.color;
                      return (
                        <button
                          key={option.id}
                          className={`option-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleFilterAnswer(question.id, option.id)}
                          style={hasColor ? { '--option-color': option.color } as React.CSSProperties : undefined}
                        >
                          <div className="option-icon">
                            <i className={option.icon}></i>
                          </div>
                          <div className="option-content">
                            <span className="option-label">{option.label}</span>
                          </div>
                          {isSelected && (
                            <div className="option-check">
                              <i className="fas fa-check-circle"></i>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="adhoc-entry" data-testid="adhoc-from-category">
                    <p className="form-help">For a one-off multi-day job that is not in the catalogue:</p>
                    <button
                      type="button"
                      className="package-card adhoc-package-card"
                      onClick={startAdhocPackage}
                      data-testid="create-adhoc-package-early"
                    >
                      <div className="package-card-header">
                        <h4>Create an ad hoc package</h4>
                      </div>
                      <div className="package-card-content">
                        <p className="package-short">
                          Skip the catalogue and build coverage day by day with its own hours and price.
                        </p>
                      </div>
                    </button>
                  </div>
                  {question.options.length === 0 ? (
                    <>
                      {renderPackageCards()}
                      {renderPackageSetup()}
                    </>
                  ) : null}
                </div>
              );
            })()}
          </div>
        )}

        {/* Package Selection Step */}
        {currentStep === filterStepCount && (
          <div className="package-selection-step">
            <p className="section-lead">
              Choose a catalogue package, create a one-off ad hoc package for multi-day work, or set up a new catalogue package below.
            </p>
            {renderPackageCards()}
            {renderPackageSetup()}
          </div>
        )}

        {/* Currency Selection Step */}
        {currentStep === filterStepCount + 1 && selectedPackage && (
          <div className="currency-selection-step">
            <div className="options-grid">
              {currencies.map((currency) => (
                <button
                  key={currency.value}
                  className={`option-card ${selectedCurrency === currency.value ? 'selected' : ''}`}
                  onClick={() => handleCurrencySelect(currency.value)}
                >
                  <div className="option-icon">
                    <i className={currency.icon}></i>
                  </div>
                  <div className="option-content">
                    <span className="option-label">{currency.label}</span>
                    <span className="option-description">{getCurrencySymbol(currency.value)}</span>
                  </div>
                  {selectedCurrency === currency.value && (
                    <div className="option-check">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add-ons Selection Step (always show if we have global addons or package addons) */}
        {(() => {
          const currencyStep = filterStepCount + 1;
          if (!selectedPackage) return null;
          const packageAddons = getPackageAddons(selectedPackage);
          const additionalAddons = getAdditionalAddons(selectedPackage);
          
          // DEBUG: Log what we're working with - FULL DETAILS for comparison
          // Only log once per render to avoid spam
          if (!addonsDebugLogged) {
            addonsDebugLogged = true;
            console.log('🔍 DEBUG Add-ons Step - FULL DETAILS:', {
              selectedPackage: selectedPackage.name,
              selectedPackageId: selectedPackage.id,
              packageAddonsCount: packageAddons.length,
              packageAddons: JSON.parse(JSON.stringify(packageAddons)),
              additionalAddonsCount: additionalAddons.length,
              additionalAddons: JSON.parse(JSON.stringify(additionalAddons)),
              globalAddonsCount: globalAddons.length,
              globalAddons: JSON.parse(JSON.stringify(globalAddons)),
              selectedPackageAddons: JSON.parse(JSON.stringify(selectedPackage.addons || [])),
              overriddenPackageAddons: {
                removed: overriddenPackageAddons.removed,
                added: JSON.parse(JSON.stringify(overriddenPackageAddons.added)),
              },
              environment: window.location.hostname,
              timestamp: new Date().toISOString()
            });
            // Reset after 1 second to allow re-logging on updates
            setTimeout(() => { addonsDebugLogged = false; }, 1000);
          }
          
          // Show addon step if we have package addons or additional addons
          const shouldShow = currentStep === currencyStep + 1 && (packageAddons.length > 0 || additionalAddons.length > 0);
          if (!shouldShow) return null;
          
          return (
            <div className="addons-selection-step">
              <h3>Package Add-ons & Extras</h3>
              <p className="step-description">
                {packageAddons.length > 0 
                  ? 'These addons are included in your package. You can deselect them or add additional services below.'
                  : 'Add additional services to your package'}
              </p>
              
              {/* Package Addons Section */}
              {packageAddons.length > 0 && (
                <div className="addons-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 className="addons-section-title" style={{ margin: 0 }}>
                      <i className="fas fa-check-circle" style={{ color: 'var(--admin-ink)', marginRight: '8px' }}></i>
                      Package extras
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const name = prompt('Enter addon name:');
                        if (name && name.trim()) {
                          const price = parseFloat(prompt('Enter price (0 for included):') || '0');
                          const pricingType = prompt('Pricing type (fixed/per-hour/per-each):') || 'fixed';
                          const description = prompt('Enter description (optional):') || '';
                          
                          const newAddon: SelectedAddOn = {
                            id: `custom-${Date.now()}`,
                            name: name.trim(),
                            price: price,
                            currency: selectedCurrency,
                            pricingType: pricingType as 'fixed' | 'per-hour' | 'per-each',
                            description: description,
                            quantity: pricingType === 'per-each' ? 1 : undefined,
                            hours: pricingType === 'per-hour' ? 1 : undefined,
                          };
                          
                          setOverriddenPackageAddons(prev => ({
                            ...prev,
                            added: [...prev.added, newAddon]
                          }));
                          setSelectedAddOns(prev => [...prev, newAddon]);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--admin-button)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Add custom addon"
                    >
                      <i className="fas fa-plus"></i>
                      <span>Add Custom</span>
                    </button>
                  </div>
                  <div className="addons-rows" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '15px' }}>
                    {packageAddons.map((addon, idx) => {
                      const selectedAddon = selectedAddOns.find(a => a.id === addon.id || (a.id === undefined && a.name === addon.name));
                      const isSelected = !!selectedAddon;
                      const pricingType = addon.pricingType || 'fixed';
                      const addonKey = addon.id || addon.name;
                      const isCustom = addon.id?.startsWith('custom-') || overriddenPackageAddons.added.some(a => (a.id || a.name) === addonKey);
                      const isFromPackage = selectedPackage?.addons?.some(a => (a.id || a.name) === addonKey);
                      
                      return (
                        <div
                          key={addon.id || `pkg-${idx}`}
                          className={`addon-row ${isSelected ? 'selected' : ''}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 15px',
                            border: `1px solid ${isSelected ? 'var(--admin-ink)' : '#e0e0e0'}`,
                            borderRadius: '6px',
                            backgroundColor: isSelected ? 'var(--admin-soft)' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gap: '15px',
                            position: 'relative'
                          }}
                          onClick={() => handleAddOnToggle(addon)}
                        >
                          {/* Remove button for package addons */}
                          {isFromPackage && !isCustom && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAddOns(prev => prev.filter(item => (item.id || item.name) !== addonKey));
                              }}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '160px', // Position to the left of price field (150px minWidth + 10px gap)
                                padding: '4px 6px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                zIndex: 10,
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Remove from package"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                          {/* Remove button for custom addons */}
                          {isCustom && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOverriddenPackageAddons(prev => ({
                                  ...prev,
                                  added: prev.added.filter(a => (a.id || a.name) !== addonKey)
                                }));
                                setSelectedAddOns(prev => prev.filter(item => (item.id || item.name) !== addonKey));
                              }}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '160px', // Position to the left of price field
                                padding: '4px 6px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                zIndex: 10,
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Remove custom addon"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', minWidth: '30px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleAddOnToggle(addon)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: 500, fontSize: '1rem', color: '#2C2C2C' }}>{addon.name}</span>
                              {isSelected && (
                                <i className="fas fa-check-circle" style={{ color: 'var(--admin-ink)', fontSize: '0.9rem' }}></i>
                              )}
                            </div>
                            {addon.description && (
                              <span style={{ fontSize: '0.85rem', color: '#6C757D', fontStyle: 'italic' }}>
                                {addon.description}
                              </span>
                            )}
                            {isSelected && (
                              <div style={{ display: 'flex', gap: '15px', marginTop: '8px', alignItems: 'center' }}>
                                {pricingType === 'per-hour' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{ fontSize: '0.85rem', color: '#6C757D' }}>Hours:</label>
                                    <input
                                      type="number"
                                      min="0.5"
                                      step="0.5"
                                      value={selectedAddon?.hours || 1}
                                      onChange={(e) => handleAddonHoursChange(addon.id, addon.name, parseFloat(e.target.value) || 0.5)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '80px',
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem'
                                      }}
                                    />
                                  </div>
                                )}
                                {pricingType === 'per-each' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{ fontSize: '0.85rem', color: '#6C757D' }}>Quantity:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={selectedAddon?.quantity || 1}
                                      onChange={(e) => handleAddonQuantityChange(addon.id, addon.name, parseInt(e.target.value) || 1)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '80px',
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem'
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '150px', gap: '4px' }}>
                            {(() => {
                              const addonKey = addon.id || addon.name;
                              const overridePrice = overriddenPackageAddons.priceOverrides.get(addonKey);
                              const baseConvertedPrice = convertedAddonPrices.get(addonKey) ?? addon.price ?? 0;
                              const displayPrice = overridePrice !== undefined ? overridePrice : baseConvertedPrice;
                              const isPriceOverridden = overriddenPackageAddons.priceOverrides.has(addonKey);
                              const originalPrice = selectedPackage?.addons?.find(a => (a.id || a.name) === addonKey)?.price;
                              const isDiscounted = isPriceOverridden && originalPrice && (addon.price ?? 0) < originalPrice;
                              const inputValue = addonPriceInputs.get(addonKey) ?? displayPrice.toString();
                              
                              if (pricingType === 'fixed') {
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    {isPriceOverridden && (
                                      <span style={{ fontSize: '0.7rem', color: isDiscounted ? 'var(--admin-ink)' : 'var(--admin-muted)', fontWeight: 500 }}>
                                        {isDiscounted ? 'Discounted' : 'Override'}
                                      </span>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={inputValue}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setAddonPriceInputs(prev => {
                                            const next = new Map(prev);
                                            next.set(addonKey, val);
                                            return next;
                                          });
                                          const parsed = parseFloat(val);
                                          if (!Number.isNaN(parsed)) {
                                            handleAddonPriceOverride(addon.id, addon.name, parsed);
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          width: '80px',
                                          padding: '4px 6px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          fontSize: '0.85rem',
                                          textAlign: 'right'
                                        }}
                                        title="Edit price"
                                      />
                                      <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                        {selectedCurrency}
                                      </span>
                                    </div>
                                    {displayPrice === 0 && (
                                      <span style={{ fontWeight: 500, color: 'var(--admin-ink)', fontSize: '0.85rem' }}>
                                        Included
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              if (pricingType === 'per-hour' && displayPrice > 0) {
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    {isPriceOverridden && (
                                      <span style={{ fontSize: '0.7rem', color: isDiscounted ? 'var(--admin-ink)' : 'var(--admin-muted)', fontWeight: 500 }}>
                                        {isDiscounted ? 'Discounted' : 'Override'}
                                      </span>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={inputValue}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setAddonPriceInputs(prev => {
                                            const next = new Map(prev);
                                            next.set(addonKey, val);
                                            return next;
                                          });
                                          const parsed = parseFloat(val);
                                          if (!Number.isNaN(parsed)) {
                                            handleAddonPriceOverride(addon.id, addon.name, parsed);
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          width: '80px',
                                          padding: '4px 6px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          fontSize: '0.85rem',
                                          textAlign: 'right'
                                        }}
                                        title="Edit price per hour"
                                      />
                                      <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                        {selectedCurrency} /hr
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                              if (pricingType === 'per-each' && displayPrice > 0) {
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    {isPriceOverridden && (
                                      <span style={{ fontSize: '0.7rem', color: isDiscounted ? 'var(--admin-ink)' : 'var(--admin-muted)', fontWeight: 500 }}>
                                        {isDiscounted ? 'Discounted' : 'Override'}
                                      </span>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={displayPrice}
                                        onChange={(e) => {
                                          const newPrice = parseFloat(e.target.value) || 0;
                                          handleAddonPriceOverride(addon.id, addon.name, newPrice);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          width: '80px',
                                          padding: '4px 6px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          fontSize: '0.85rem',
                                          textAlign: 'right'
                                        }}
                                        title="Edit price per each"
                                      />
                                      <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                        {selectedCurrency} each
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                              if (displayPrice === 0 || !addon.price) {
                                return (
                                  <span style={{ fontWeight: 500, color: 'var(--admin-ink)', fontSize: '0.9rem' }}>
                                    Included
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Additional Addons Section (rows, same layout as package addons) */}
              {additionalAddons.length > 0 && (
                <div className="addons-section" style={{ marginTop: packageAddons.length > 0 ? '30px' : '0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 className="addons-section-title" style={{ margin: 0 }}>
                      <i className="fas fa-plus-circle" style={{ color: '#2196F3', marginRight: '8px' }}></i>
                      Additional Services
                    </h4>
                  </div>
                  <div className="addons-rows" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '15px' }}>
                    {additionalAddons.map((addon, idx) => {
                      const selectedAddon = selectedAddOns.find(a => a.id === addon.id || (a.id === undefined && a.name === addon.name));
                      const isSelected = !!selectedAddon;
                      const pricingType = addon.pricingType || 'fixed';
                      const addonKey = addon.id || addon.name;
                      const displayPrice = convertedAddonPrices.get(addonKey) ?? addon.price ?? 0;
                      const currentInputValue = addonPriceInputs.has(addonKey)
                        ? addonPriceInputs.get(addonKey)
                        : displayPrice.toString();

                      return (
                        <div
                          key={addon.id || `additional-${idx}`}
                          className={`addon-row ${isSelected ? 'selected' : ''}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 15px',
                            border: `1px solid ${isSelected ? 'var(--admin-ink)' : '#e0e0e0'}`,
                            borderRadius: '6px',
                            backgroundColor: isSelected ? 'var(--admin-soft)' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gap: '15px',
                            position: 'relative'
                          }}
                          onClick={() => handleAddOnToggle(addon)}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleAddOnToggle(addon)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600 }}>{addon.name}</span>
                              {addon.description && (
                                <span style={{ color: '#6C757D', fontSize: '0.9rem' }}>{addon.description}</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                              {pricingType === 'per-hour' && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <label style={{ fontSize: '0.9rem', color: '#555' }}>Hours:</label>
                                    <input
                                      type="number"
                                      min="0.5"
                                      step="0.5"
                                      value={selectedAddon?.hours || 1}
                                      onChange={(e) => handleAddonHoursChange(addon.id, addon.name, parseFloat(e.target.value) || 0.5)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '70px',
                                        padding: '4px 6px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                      }}
                                      title="Edit hours"
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={currentInputValue}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        setAddonPriceInputs((prev: Map<string, string>) => new Map(prev).set(addonKey, newValue));
                                        const newPrice = parseFloat(newValue);
                                        if (!isNaN(newPrice)) {
                                          handleAddonPriceOverride(addon.id, addon.name, newPrice);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '80px',
                                        padding: '4px 6px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        textAlign: 'right'
                                      }}
                                      title="Edit price per hour"
                                    />
                                    <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                      {selectedCurrency} / hr
                                    </span>
                                  </div>
                                </>
                              )}
                              {pricingType === 'per-each' && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <label style={{ fontSize: '0.9rem', color: '#555' }}>Qty:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={selectedAddon?.quantity || 1}
                                      onChange={(e) => handleAddonQuantityChange(addon.id, addon.name, parseInt(e.target.value) || 1)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '70px',
                                        padding: '4px 6px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                      }}
                                      title="Edit quantity"
                                    />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={currentInputValue}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        setAddonPriceInputs((prev: Map<string, string>) => new Map(prev).set(addonKey, newValue));
                                        const newPrice = parseFloat(newValue);
                                        if (!isNaN(newPrice)) {
                                          handleAddonPriceOverride(addon.id, addon.name, newPrice);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: '80px',
                                        padding: '4px 6px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        textAlign: 'right'
                                      }}
                                      title="Edit price per each"
                                    />
                                    <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                      {selectedCurrency} each
                                    </span>
                                  </div>
                                </>
                              )}
                              {pricingType === 'fixed' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={currentInputValue}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      setAddonPriceInputs((prev: Map<string, string>) => new Map(prev).set(addonKey, newValue));
                                      const newPrice = parseFloat(newValue);
                                      if (!isNaN(newPrice)) {
                                        handleAddonPriceOverride(addon.id, addon.name, newPrice);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '80px',
                                      padding: '4px 6px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '0.85rem',
                                      textAlign: 'right'
                                    }}
                                    title="Edit price"
                                  />
                                  <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>
                                    {selectedCurrency}
                                  </span>
                                </div>
                              )}
                              {displayPrice === 0 && (
                                <span style={{ fontWeight: 500, color: 'var(--admin-ink)', fontSize: '0.9rem' }}>
                                  Included
                                </span>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <div style={{ color: 'var(--admin-ink)', fontSize: '1rem' }}>
                              <i className="fas fa-check-circle"></i>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Client Details Step */}
        {(onEnquiryPage ? Boolean(selectedPackage) : currentStep === getClientDetailsStep()) && (
          <div className="client-details-step" data-testid="enquiry-details">
            {onEnquiryPage ? <h3 className="enquiry-details-title">Details</h3> : null}
            <div className="client-identity">
              <div className="form-section">
                <label htmlFor="client-name">Client name</label>
                <input type="text" id="client-name" value={clientName} onChange={(e) => { setClientName(e.target.value); setContactError(''); }} placeholder="Full name" className="form-input" />
              </div>
              <div className="form-section">
                <label htmlFor="client-email">Email address</label>
                <input type="email" id="client-email" value={recipientEmail} onChange={(e) => { setRecipientEmail(e.target.value); setContactError(''); }} placeholder="name@example.com" className="form-input" />
              </div>
              <div className="form-section phone-fields">
                <div>
                  <label htmlFor="country-code">Country</label>
                  <CountryCodeSelect id="country-code" value={countryCode} onChange={setCountryCode} />
                </div>
                <div>
                  <label htmlFor="client-phone">Mobile / WhatsApp</label>
                  <input id="client-phone" value={clientPhone} onChange={(e) => { setClientPhone(e.target.value); setContactError(''); }} placeholder="082 123 4567" className="form-input" inputMode="tel" />
                </div>
              </div>
              <div className="form-section">
                <label htmlFor="contact-preference">Preferred contact</label>
                <select id="contact-preference" className="form-input" value={contactPreference} onChange={(e) => { setContactPreference(e.target.value as 'email' | 'whatsapp' | 'either'); setContactError(''); }}>
                  <option value="either">Email or WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
              <div className="form-section">
                <label htmlFor="company-name">Company (optional)</label>
                <input id="company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="form-input" />
              </div>
              <div className="form-section billing-address">
                <label htmlFor="billing-address">Billing address (optional)</label>
                <textarea
                  id="billing-address"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  className="form-input"
                  rows={4}
                  maxLength={500}
                  placeholder={'12 Loop Street\nGardens\nCape Town\n8001'}
                />
                <small className="form-help">For invoicing. Printed on the quotation only when it is filled in. This is not the shoot location.</small>
              </div>
            </div>
            {adhocPackage || sessions.length > 1 ? (
              <div className="form-section">
                {renderSessionsEditor({
                  showPrices: Boolean(adhocPackage),
                  title: adhocPackage ? 'Ad hoc sessions' : 'Shoot sessions',
                })}
              </div>
            ) : (
              <>
                <div className="form-section">
                  <PreferredDateField
                    value={preferredDate}
                    onChange={(value) => {
                      setPreferredDate(value);
                      if (sessions.length === 1) updateSession(sessions[0].id, { date: value });
                    }}
                    label="Preferred shoot date"
                    dateLabel="Client has a date"
                    laterLabel="Decide later"
                  />
                  <small className="form-help">The client sees this on their quotation. Carmen confirms the actual date once the deposit is in.</small>
                </div>
                <div className="form-section">
                  <PreferredTimeField
                    value={preferredTime}
                    onChange={(value) => {
                      setPreferredTime(value);
                      if (sessions.length === 1) updateSession(sessions[0].id, { startTime: value });
                    }}
                  />
                </div>
                <div className="form-section">
                  <ShootLocationField
                    value={preferredLocation}
                    onChange={(value) => {
                      setPreferredLocation(value);
                      if (sessions.length === 1) updateSession(sessions[0].id, { location: value });
                    }}
                    label="Shoot location"
                    placeLabel="Client has a place"
                    laterLabel="Decide later"
                    help="Search for the place, or type it below. Choosing a result saves a Google Maps pin. The client sees it on their quotation and can change it before accepting."
                  />
                </div>
                <div className="form-section">
                  <button type="button" className="studio-secondary" onClick={addSession} data-testid="enable-multi-day">
                    Add another day
                  </button>
                  <small className="form-help">For events that run over more than one day with different hours.</small>
                </div>
              </>
            )}
            <div className="form-section">
              <label htmlFor="personal-message">Personal message (optional)</label>
              <LetterComposer id="personal-message" value={personalMessage} onChange={setPersonalMessage} />
              <small className="form-help">Bold, italic, underline, bullets, numbering, and text size appear on the client quotation, the preview, and the PDF. A blank line starts a new paragraph. Page break, a line with ---, or two blank lines together, starts a new page.</small>
            </div>
            <div className="form-section" data-testid="quote-guides">
              <fieldset className="quote-guides-fieldset">
                <legend>Include with this quotation</legend>
                {guideLibrary.length === 0 ? (
                  <p className="form-help">No client guides yet. Add PDFs under Settings → Client guides.</p>
                ) : (
                  <ul className="quote-guides-list">
                    {guideLibrary.map((guide) => {
                      const checked = selectedGuideIds.includes(guide.id);
                      return (
                        <li key={guide.id}>
                          <label>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setSelectedGuideIds((current) => (
                                  checked
                                    ? current.filter((id) => id !== guide.id)
                                    : current.concat([guide.id])
                                ));
                              }}
                            />
                            <span>{guide.title}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </fieldset>
              <small className="form-help">The client can open these from their quotation page and PDF when they want.</small>
            </div>
            {contactError ? (
              <p className="studio-error" role="alert" data-testid="contact-error">
                {contactError} Fix it here, then go on to Review and issue again.
              </p>
            ) : null}
            <small className="form-help">One email address or mobile number is needed before the quotation is issued. +27 is the usual code for South Africa.</small>
            {onEnquiryPage ? (
              <div className="enquiry-details-actions">
                <button type="button" className="btn-primary" onClick={() => setCurrentStep(totalSteps - 1)} disabled={!selectedPackage}>
                  Review quotation
                </button>
                <button type="button" className="btn-secondary" onClick={editingIssuedQuote ? handleSaveChanges : handleSaveDraft} disabled={!selectedPackage || saving}>
                  {saving ? 'Saving…' : editingIssuedQuote ? 'Save changes' : 'Save draft'}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Final Step: Review & Generate */}
        {currentStep === totalSteps - 1 && (
          <div className="review-step">
            {selectedPackage && (
              <>
                <div className="review-summary">
                  <h4>Quote Summary</h4>
                  <div className="review-edit-links" role="group" aria-label="Change something before issuing">
                    <button type="button" className="btn-secondary btn-sm" onClick={() => editStep(getClientDetailsStep())}>
                      <i className="fas fa-pen"></i> Client details
                    </button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => editStep(filterStepCount)}>
                      <i className="fas fa-pen"></i> Package
                    </button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => editStep(filterStepCount + 1)}>
                      <i className="fas fa-pen"></i> Currency
                    </button>
                    {hasExtrasStep && (
                      <button type="button" className="btn-secondary btn-sm" onClick={() => editStep(filterStepCount + 2)}>
                        <i className="fas fa-pen"></i> Extras
                      </button>
                    )}
                  </div>
                  {clientName && (
                    <div className="summary-item">
                      <span className="summary-label">Client:</span>
                      <span className="summary-value">{clientName}</span>
                    </div>
                  )}
                  {(recipientEmail || clientPhone) && (
                    <div className="summary-item">
                      <span className="summary-label">Contact:</span>
                      <span className="summary-value">{[recipientEmail, clientPhone ? `${countryCode} ${clientPhone}` : ''].filter(Boolean).join(' · ')}</span>
                    </div>
                  )}
                  <div className="summary-item">
                    <span className="summary-label">Preference:</span>
                    <span className="summary-value">{contactPreference === 'email' ? 'Email' : contactPreference === 'whatsapp' ? 'WhatsApp' : 'Email or WhatsApp'}</span>
                  </div>
                  {billingAddress.trim() ? (
                    <div className="summary-item">
                      <span className="summary-label">Billing address:</span>
                      <span className="summary-value billing-address">{billingAddress.trim()}</span>
                    </div>
                  ) : null}
                  {effectiveSessions().length > 1 ? (
                    <div className="summary-item">
                      <span className="summary-label">Schedule:</span>
                      <span className="summary-value">
                        {effectiveSessions().map((session, index) => (
                          <span key={session.id} style={{ display: 'block' }}>
                            {formatSessionLine(session, { index, total: effectiveSessions().length, fallbackLocation: placesArePerDay ? null : preferredLocation })}
                            {session.notes?.trim() ? ` — ${session.notes.trim()}` : ''}
                          </span>
                        ))}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="summary-item">
                        <span className="summary-label">Shoot date:</span>
                        <span className="summary-value">{[formatPreferredDate(preferredDate), preferredTime].filter(Boolean).join(' at ') || 'To be arranged'}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Location:</span>
                        <span className="summary-value">{[preferredLocation.name, preferredLocation.address].filter(Boolean).join(', ') || 'To be agreed'}{preferredLocation.mapsUrl ? ' · map link added' : ''}</span>
                      </div>
                    </>
                  )}
                  {selectedGuideIds.length > 0 ? (
                    <div className="summary-item">
                      <span className="summary-label">Guides:</span>
                      <span className="summary-value">
                        {selectedGuideIds
                          .map((id) => guideLibrary.find((guide) => guide.id === id)?.title || id)
                          .join(', ')}
                      </span>
                    </div>
                  ) : null}
                  <div className="summary-item">
                    <span className="summary-label">Package:</span>
                    <span className="summary-value">{selectedPackage.name}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Currency:</span>
                    <span className="summary-value">{selectedCurrency}</span>
                  </div>
                  
                  {/* Package Price Override */}
                  <div className="form-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <label htmlFor="override-package-price" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <i className="fas fa-edit" style={{ color: 'var(--admin-button)' }}></i>
                      <span>Package Price Override (Optional)</span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="number"
                        id="override-package-price"
                        min="0"
                        step="0.01"
                        value={overridePackagePrice !== null ? overridePackagePrice : ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseFloat(e.target.value);
                          if (value !== null && value >= 0) {
                            setOverridePackagePrice(value);
                            setOverridePackageCurrency(selectedCurrency);
                          } else {
                            setOverridePackagePrice(null);
                            setOverridePackageCurrency(null);
                          }
                        }}
                        placeholder={formatCurrency(displayBasePrice || 0, selectedCurrency)}
                        className="form-input"
                        style={{ flex: 1, maxWidth: '200px' }}
                      />
                      <span style={{ color: '#6C757D', fontSize: '0.9rem' }}>
                        {selectedCurrency}
                      </span>
                      {overridePackagePrice !== null && (
                        <button
                          type="button"
                          onClick={() => {
                            setOverridePackagePrice(null);
                            setOverridePackageCurrency(null);
                          }}
                          style={{
                            padding: '5px 10px',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                          title="Reset to package price"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <small className="form-help" style={{ display: 'block', marginTop: '5px' }}>
                      {overridePackagePrice !== null
                        ? adhocPackage
                          ? `Charged as well as the day prices of ${formatCurrency(sessions.reduce((sum, session) => sum + (Number(session.priceMajor) > 0 ? Number(session.priceMajor) : 0), 0), selectedCurrency)}. Each day keeps its own price.`
                          : `Replaces the package price of ${formatCurrency(displayBasePrice || selectedPackage.price || 0, selectedCurrency)}.`
                        : adhocPackage
                          ? 'Leave empty to charge the price entered on each day. The package stays included.'
                          : 'Leave empty to use the package price.'}
                    </small>
                  </div>
                  
                  {/* Discount Input */}
                  <div className="form-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <label htmlFor="discount" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <i className="fas fa-percent" style={{ color: 'var(--admin-button)' }}></i>
                      <span>Discount on the quotation (%)</span>
                    </label>
                    <input
                      type="number"
                      id="discount"
                      min="0"
                      max="100"
                      step="0.1"
                      value={discount}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setDiscount(Math.max(0, Math.min(100, value)));
                      }}
                      placeholder="0"
                      className="form-input"
                      style={{ width: '100%', maxWidth: '200px' }}
                    />
                    <small className="form-help" style={{ display: 'block', marginTop: '5px' }}>
                      Optional. This percentage is taken off the quotation total.
                    </small>
                  </div>

                  <div className="form-section" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <label htmlFor="deposit" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <i className="fas fa-percent" style={{ color: 'var(--admin-button)' }}></i>
                      <span>Deposit required to secure the date (%)</span>
                    </label>
                    <input
                      type="number"
                      id="deposit"
                      min="0"
                      max="100"
                      step="0.1"
                      value={depositPercent}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setDepositPercent(Math.max(0, Math.min(100, value)));
                      }}
                      className="form-input"
                      style={{ width: '100%', maxWidth: '200px' }}
                    />
                    <small className="form-help" style={{ display: 'block', marginTop: '5px' }}>
                      Defaults to 50% of the rand total. Use 100% when the full amount is due now.
                    </small>
                  </div>
                  
                  {/* Price Breakdown — every row is a priced line, so the amounts add up to the subtotal. */}
                  <div className="price-breakdown" data-testid="price-overview" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    {isCalculating || !pricingSnapshot ? (
                      <p className="summary-item">Calculating...</p>
                    ) : pricingSnapshot.lines.map((line) => (
                      <div key={line.id} className="summary-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span className="summary-label" style={{ fontWeight: 500 }}>{line.name}</span>
                            {line.kind === 'package' && overridePackagePrice !== null && (
                              <span style={{
                                fontSize: '0.75rem',
                                padding: '2px 6px',
                                background: 'var(--admin-muted)',
                                color: 'white',
                                borderRadius: '3px',
                                fontWeight: 500,
                              }}>
                                Price override
                              </span>
                            )}
                          </div>
                          {line.description ? (
                            <span style={{ fontSize: '0.85rem', color: '#6C757D' }}>{line.description}</span>
                          ) : null}
                        </div>
                        <span className="summary-value" style={{ fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {line.complimentary ? 'Included' : formatQuoteMoney(line.quoteExtendedMinor, pricingSnapshot.currency)}
                        </span>
                      </div>
                    ))}
                    
                    {/* Subtotal */}
                    <div className="summary-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: '8px', borderTop: '2px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
                      <span className="summary-label" style={{ fontWeight: 600 }}>Subtotal:</span>
                      <span className="summary-value" style={{ fontWeight: 600 }}>
                        {isCalculating || !pricingSnapshot ? 'Calculating...' : formatQuoteMoney(pricingSnapshot.quoteSubtotalMinor, pricingSnapshot.currency)}
                      </span>
                    </div>
                    
                    {/* Discount */}
                    {pricingSnapshot && pricingSnapshot.quoteDiscountMinor > 0 && (
                      <div className="summary-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0', color: 'var(--admin-ink)' }}>
                        <span className="summary-label">Discount ({pricingSnapshot.discountPercent}%):</span>
                        <span className="summary-value">
                          -{formatQuoteMoney(pricingSnapshot.quoteDiscountMinor, pricingSnapshot.currency)}
                        </span>
                      </div>
                    )}
                    
                    {/* Total */}
                    <div className="summary-total" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: '8px', borderTop: '2px solid var(--admin-button)', fontWeight: 'bold', fontSize: '1.1em' }}>
                      <span className="summary-label">Price:</span>
                      <span className="summary-value" style={{ color: 'var(--admin-button)' }}>
                        {isCalculating || !pricingSnapshot ? 'Calculating...' : formatQuoteMoney(pricingSnapshot.quoteTotalMinor, pricingSnapshot.currency)}
                      </span>
                    </div>
                    {pricingSnapshot && (
                      <>
                        <div className="summary-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                          <span className="summary-label">Deposit ({pricingSnapshot.depositPercent}%) to secure the date:</span>
                          <span className="summary-value">{formatQuoteMoney(pricingSnapshot.depositMinor, 'R')}</span>
                        </div>
                        <div className="summary-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                          <span className="summary-label">Balance, payable on completion:</span>
                          <span className="summary-value">{formatQuoteMoney(pricingSnapshot.balanceMinor, 'R')}</span>
                        </div>
                        {pricingSnapshot.currency !== 'R' && (
                          <div className="summary-item" style={{ padding: '8px 0', color: '#6C757D', fontSize: '0.9rem' }}>
                            Rand settlement: {formatQuoteMoney(pricingSnapshot.zarTotalMinor, 'R')}. This is the final {pricingSnapshot.currency} total converted at the recorded market rate. Card payments are processed in rand.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="review-actions">
                  {editingIssuedQuote ? (
                    <button
                      type="button"
                      onClick={handleSaveChanges}
                      className="btn-primary"
                      disabled={!selectedPackage || isCalculating || saving}
                      data-testid="save-quote-changes"
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  ) : isIssued ? null : (
                    <button
                      onClick={handleIssueQuote}
                      className="btn-primary"
                      disabled={!selectedPackage || isCalculating || saving}
                      data-testid="issue-quotation"
                    >
                      {saving ? 'Issuing…' : 'Issue quotation'}
                    </button>
                  )}
                  <button
                    onClick={generateQuote}
                    className="btn-secondary"
                    disabled={!selectedPackage || isCalculating || saving}
                  >
                    Preview quotation
                  </button>
                  {editingIssuedQuote || isIssued ? null : (
                    <button
                      onClick={handleSaveDraft}
                      className="btn-secondary"
                      disabled={!selectedPackage || isCalculating || saving}
                    >
                      Save draft
                    </button>
                  )}
                </div>
                {saveMessage && (
                  <p className="form-help" style={{ marginTop: '12px' }} role="status" data-testid="review-status">{saveMessage}</p>
                )}
                {issuedReach ? (
                  <div ref={issuedReachRef} style={{ marginTop: '12px' }}>
                    <ReplyReach
                      sent={issuedReach.sent}
                      clientName={clientName}
                      phoneE164={clientPhoneE164()}
                      clientLink={clientLink}
                      onRecord={(text) => recordIssuedHandoff(issuedReach.quoteId, text)}
                      onClose={() => {
                        setIssuedReach(null);
                        if (onDone) onDone({ id: issuedReach.quoteId } as StoredQuote);
                      }}
                    />
                  </div>
                ) : null}
                {clientLink && !issuedReach && (
                  <p className="form-help" style={{ marginTop: '8px' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => navigator.clipboard.writeText(clientLink)}>Copy client link</button>
                    {onDone ? (
                      <>
                        {' '}
                        <button type="button" className="btn-secondary btn-sm" onClick={() => onDone()}>{returnLabel}</button>
                      </>
                    ) : null}
                  </p>
                )}

                {showPreview && quoteHTML && (
                  <div className="quote-preview-section">
                    <div className="preview-header">
                      <h4>Quote Preview</h4>
                      <div className="preview-actions">
                        <button
                          onClick={() => {
                            setShowPreview(false);
                            window.setTimeout(() => {
                              document.querySelector('.review-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 0);
                          }}
                          className="btn-secondary btn-sm"
                          title="Close the preview to change client details, package, currency or extras"
                        >
                          <i className="fas fa-pen"></i> Back to edit
                        </button>
                        <button
                          onClick={handleCopyQuote}
                          className="btn-secondary btn-sm"
                          title="Copy quote to clipboard"
                        >
                          <i className="fas fa-copy"></i> Copy
                        </button>
                        <button
                          onClick={handleDownloadPdf}
                          className="btn-secondary btn-sm"
                          disabled={pdfBusy}
                          title="Download a PDF you can print or send"
                        >
                          <i className="fas fa-file-pdf"></i> {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
                        </button>
                        <button
                          onClick={handlePrintQuote}
                          className="btn-secondary btn-sm"
                          title="Print quote"
                        >
                          <i className="fas fa-print"></i> Print
                        </button>
                      </div>
                    </div>
                    {pdfNote ? <p className="form-help" role="status">{pdfNote}</p> : null}
                    <div className="preview-content">
                      <iframe
                        srcDoc={quoteHTML}
                        title="Quote Preview"
                        className="quote-preview-iframe"
                      />
                    </div>
                    <div className="email-section">
                      <label htmlFor="recipient-email">Recipient email</label>
                      <input
                        type="email"
                        id="recipient-email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="client@example.com"
                        className="form-input email-recipient-input"
                      />
                      {clientLink ? (
                        <label htmlFor="client-quote-link">Client link included in the email</label>
                      ) : (
                        <p className="form-help">Issue the quotation first. The email then includes the client's private link.</p>
                      )}
                      {clientLink ? (
                        <input id="client-quote-link" className="form-input email-recipient-input" readOnly value={clientLink} onFocus={(event) => event.currentTarget.select()} />
                      ) : null}
                      <button
                        onClick={handleSendEmail}
                        className="btn-primary email-send-button"
                        disabled={!recipientEmail.trim() || !clientLink}
                      >
                        Open in your mail app
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="wizard-footer">
        <div className="wizard-actions">
          {currentStep > 0 && (
            <button className="btn-secondary" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
              <span>Back</span>
            </button>
          )}
          {currentStep >= filterStepCount + 1 && currentStep < totalSteps - 1 && selectedPackage && (
            <button 
              className="btn-primary" 
              onClick={handleNext}
              disabled={currentStep === filterStepCount + 1 && !selectedCurrency}
            >
              <span>Continue</span>
              <i className="fas fa-arrow-right"></i>
            </button>
          )}
          {fromEnquiry ? null : (
          <button className="btn-secondary" onClick={resetQuote}>
            <i className="fas fa-redo"></i>
            <span>Start Over</span>
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;
